import { CommonModule, DatePipe } from "@angular/common";
import { Component, computed, effect, ElementRef, inject, OnInit, output, signal, ViewChild, WritableSignal } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from "@angular/forms";
import { noWhitespaceValidator } from '../../utils/no-whitespace.validator';
import { Router } from '@angular/router';
import { MatExpansionModule } from "@angular/material/expansion";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker'
import { provideNativeDateAdapter } from '@angular/material/core';
import { ConfirmationDialogComponent } from "../utils/confirmation-dialog/confirmation-dialog.component";
import { InceptionDateDialogComponent } from "../utils/inception-date-dialog/inception-date-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { MatCheckboxModule } from '@angular/material/checkbox';
import { PropertyService } from "../../services/property.service";
import { forkJoin, map, of, tap, catchError } from "rxjs";
import { MatSelectModule } from '@angular/material/select';
import { IndicationQuoteStepService } from "../../services/indicationQuoteStep.service";
import { AuthService } from "../../auth/auth.service";
import { ToastService } from "../../services/toast.service";
import { EQBD_HIGH_BPP_RESTRICTED_DESCRIPTIONS, IndicationInceptionStatus, ToastType } from "../../enums/config.enum";
import { clearanceQuoteData, LossPayeeTypeList, SLLicenseTypeList } from "../../models/RequestQuote";
import moment from 'moment';
import { PolicyDocumentsComponent } from "../policy-documents/policy-documents.component";
import { PolicyDocumentService } from "../../services/policyDocument.service";
import { SpinnerComponent } from "../utils/spinner/spinner.component";
import { PolicyAdministrationService } from "../../services/policyAdministration.service";
import { RequestQuoteService } from "../../services/requestQuote.service";
import { BrokerQuoteService } from "../../services/brokerQuote.service";
import { IndicationStatus } from '../../enums/config.enum';
import { InceptionStatusResponse } from "../../models/QuoteList";
import { MessageCenterService } from "../../services/messageCenter.service";
import { NavigationHandlerService } from "../../services/navigationHandler.service";
import { SaveConfirmationDialogComponent } from "../save-confirmation-dialog/save-confirmation-dialog.component";
import { SystemAdminService } from "../../services/system-admin.service";


@Component({
  selector: 'app-bind-issue-policy-step',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [MatExpansionModule, MatSnackBarModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, SpinnerComponent,
    CommonModule, FormsModule, ReactiveFormsModule, MatCheckboxModule, MatSelectModule, PolicyDocumentsComponent],
  templateUrl: './bind-issue-policy-step.component.html',
  styleUrl: './bind-issue-policy-step.component.scss',
})
export class BindIssuePolicyStepComponent implements OnInit {
  @ViewChild('policyStartDateInput') policyStartDateInput?: ElementRef<HTMLInputElement>;
  fb: FormBuilder = inject(FormBuilder);
  dialog = inject(MatDialog);
  _service = inject(PropertyService);
  requestQuoteService = inject(RequestQuoteService);      
  indicationQuoteService = inject(IndicationQuoteStepService);
  brokerQuoteService = inject(BrokerQuoteService);
  authService = inject(AuthService);
  messageCenterService = inject(MessageCenterService);
  submitReferralSignal = signal<boolean>(false)
  isEqbdConfirmationDisabled = signal<boolean>(false);
  commercialPropertyPremium = signal<number>(0);
  inspectionFeePremium = signal<number>(0);
  eqbdPremium = signal<number>(0);
  triaPremium = signal<number>(0);
  selectedQuote = signal<any>('');
  InceptionStatusList = signal<InceptionStatusResponse[]>([]);
  maxSurplusFeeAmount = 2500;
  /** Allows 0–2 decimal places; `\.\d{0,2}` supports values like `12.` while the user is typing. */
  private readonly currencyAmountPattern = /^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{0,2})?$/;
  
  private parseAmount(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const numeric = parseFloat(String(value).replace(/,/g, ''));
    return isNaN(numeric) ? 0 : numeric;
  }

  private feeAmountMaxValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const numeric = this.parseAmount(value);
      return numeric > this.maxSurplusFeeAmount ? { feeMaxExceeded: true } : null;
    };
  }

  // Treat zero as missing so existing required messages are shown.
  private zeroAmountRequiredValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }
      return this.parseAmount(value) === 0 ? { required: true } : null;
    };
  }
  
  bindIssuePolicyForm = this.fb.group({
    policyStartDate: ['', [Validators.required, noWhitespaceValidator]],
    PolicyEndDate: ['', [Validators.required, noWhitespaceValidator]],
    monthsDifference: ['12'],
    triaAcceptance: [null as number | null, [Validators.required, noWhitespaceValidator]],
    eqbdAcceptance: [null as number | null, [Validators.required, noWhitespaceValidator]],
    lossPayeeDetailsForm: this.fb.array([]),
    productingAgentName: ['', [Validators.required, noWhitespaceValidator]],
    ProductingAgentCompanyName : ['', [Validators.required, noWhitespaceValidator]],
    productingAgentEmail: ['', [Validators.required, noWhitespaceValidator, Validators.email]],
    producingAgentPhone: ['', [Validators.required, noWhitespaceValidator, Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)]],
    InsuredPhoneNumber: ['', [Validators.required, noWhitespaceValidator, Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)]],
    producerName: ['', [Validators.required, noWhitespaceValidator]],
    surplusNumber: ['', [Validators.required, noWhitespaceValidator]],
    surplusOther: [''],
    insuredContactName: ['', [Validators.required, noWhitespaceValidator]],
    insuredEmailAddress: ['', [Validators.required, noWhitespaceValidator, Validators.email]],
    policyFee: ['', [Validators.required, noWhitespaceValidator, this.zeroAmountRequiredValidator(), Validators.pattern(this.currencyAmountPattern), this.feeAmountMaxValidator()]],
    surplusLinesTax: ['', [Validators.required, noWhitespaceValidator, this.zeroAmountRequiredValidator(), Validators.pattern(this.currencyAmountPattern), this.feeAmountMaxValidator()]],
    producingAgentAddress: this.fb.group({
      unitNum: [''],
      streetNum: ['', [Validators.required, noWhitespaceValidator]],
      streetName: ['', [Validators.required, noWhitespaceValidator]],
      cityId: ['', [Validators.required, noWhitespaceValidator]],
      stateId: ['', [Validators.required, noWhitespaceValidator]],
      countyId: ['', [Validators.required, noWhitespaceValidator]],
      zipCode: ['', [Validators.required, noWhitespaceValidator]],
      quoteAddressId: [0],
      addressTypeId: [0],
      quoteId: ['']
    }),
    insuredMailingAddress: this.fb.group({
      unitNum: [''],
      streetNum: [''],
      streetName: [''],
      cityId: ['', [Validators.required, noWhitespaceValidator]],
      stateId: ['', [Validators.required, noWhitespaceValidator]],
      countyId: ['', [Validators.required, noWhitespaceValidator]],
      zipCode: ['', [Validators.required, noWhitespaceValidator]],
      quoteAddressId: [0],
      addressTypeId: [0],
      quoteId: ['']
    }), 
    slAddress: this.fb.group({
      unitNum: [''],
      streetNum: ['', [Validators.required, noWhitespaceValidator]],
      streetName: ['', [Validators.required, noWhitespaceValidator]],
      cityId: ['', [Validators.required, noWhitespaceValidator]],
      stateId: ['', [Validators.required, noWhitespaceValidator]],
      countyId: ['', [Validators.required, noWhitespaceValidator]],
      zipCode: ['', [Validators.required, noWhitespaceValidator]],
      quoteAddressId: [0],
      addressTypeId: [0],
      quoteId: ['']
    }),
    slTaxFeeDetails: this.fb.array([]),
    termsAndConditions: this.fb.group({
      acceptTerms: [false, Validators.requiredTrue],
      materialFacts: [false, Validators.requiredTrue],
      slTaxNotice: [false, Validators.requiredTrue]
    }),
    // New Jersey Transaction Number
    njtnSlaNumber: [''],
    njtnYear: [''],
    njtnUniqueNumber: ['', [Validators.pattern(/^\d{5}$/)]]
  });
  lossPayeeAddress = signal<FormGroup[]>([]);
  selectedOption: string = '';
  isClearanceSelected: boolean = false;
  isCompanyBranchSelected: boolean = false;
  companyBranchAddressOptions = signal<{ key: 'companyAddress' | 'branchAddress'; label: string; address: any }[]>([]);
  selectedAddress: any = {};
  lossPayeeTypeList = signal<LossPayeeTypeList[]>([]);
  SLlicenceNumberList = signal<SLLicenseTypeList[]>([]);
  surplusLinesTaxFeeOptions = signal<any[]>([]);
  propertyService = inject(PropertyService);
  premisesAddressList = signal<any[]>([]);
  clearanceAddress = signal<any[]>([]);
  masterStateList = signal<any[]>([])
  states: WritableSignal<Map<any, any[]>> = signal(new Map());
  counties: WritableSignal<Map<any, any[]>> = signal(new Map());
  cities: WritableSignal<Map<any, any[]>> = signal(new Map());
  statesPA = signal<any[]>([]);
  countiesPA = signal<any[]>([]);
  citiesPA = signal<any[]>([]);
  statesSI = signal<any[]>([]);
  countiesSI = signal<any[]>([]);
  citiesSI = signal<any[]>([]);
  statesPI = signal<any[]>([]);
  countiesPI = signal<any[]>([]);
  citiesPI = signal<any[]>([]);
  saveIndicationHomeEmitter = output();
  onBindAndIssuePolicyEmitter = output();
  searchLPState: WritableSignal<Map<number, string>> = signal(new Map());
  searchLPCounty: WritableSignal<Map<number, string>> = signal(new Map()); 
  searchLPCity: WritableSignal<Map<number, string>> = signal(new Map());
  searchPAState = signal<any>('');
  searchPACounty = signal<any>(''); 
  searchPACity = signal<any>('');
  searchSIState = signal<any>('');
  searchSICounty = signal<any>(''); 
  searchSICity = signal<any>('');
  searchPIState = signal<any>('');
  searchPICounty = signal<any>(''); 
  searchPICity = signal<any>('');
  filteredStates: WritableSignal<Map<any, any[]>> = signal(new Map());
  filteredCounty: WritableSignal<Map<any, any[]>> = signal(new Map());
  filteredCity: WritableSignal<Map<any, any[]>> = signal(new Map());
  filteredPAState = signal<any[]>([]);
  filteredSIState = signal<any[]>([]);
  filteredPIState = signal<any[]>([]);
  filteredPACounty = signal<any[]>([]);
  filteredSICounty = signal<any[]>([]);
  filteredPICounty = signal<any[]>([]);
  filteredPACity = signal<any[]>([]);
  filteredSICity = signal<any[]>([]);
  filteredPICity = signal<any[]>([]);
  today = signal(moment().format('YYYY-MM-DD'))
  toastService = inject(ToastService);
  router = inject(Router);
  monthsRange = signal(Array.from({ length: 13 }, (_, i) => i + 6)); // Create an array from 6 to 18
  policyEndDateMin = signal('');
  policyEndDateMax = signal('');
  enableViewAndDownloadDocumentSignal = signal(false);
  isDownloadEmailComplete = signal<number>(0);
  enableViewAndDownloadDocument = computed(() => 
    this.enableViewAndDownloadDocumentSignal() && this.isDownloadEmailComplete() !== 1
  );
  policyDocumentService = inject(PolicyDocumentService);
  isLoading = signal(false);  
  targetPremiumEmitter = output<boolean>();
  policyService = inject(PolicyAdministrationService);
  policyDocDownload = signal<boolean>(false);
  isReadOnly = signal(false);
  isInceptionReferralLock = signal<boolean>(false);
  showInceptionDateInlineMessage = signal<boolean>(false);
  private initialPolicyStartDateValue = '';
  maxSurplusFeeItems = 5;
  policyFeeGencodeId = signal<number | null>(null);
  surplusLinesTaxGencodeId = signal<number | null>(null);
  minStartDate!: string;
  maxStartDate!: string;
  datePipe = inject(DatePipe);
  navigationHandler = inject(NavigationHandlerService);
  navLogoClickSubscription: any;
  daysDifference: number = 0; // calculated UI-only value (not part of payload)
  systemAdminService = inject(SystemAdminService);
  private cachedBislFeeDetails: any[] = []; // Cache fee details to re-bind after gencodeIds load
  
  // New Jersey Transaction Number fields
  isNewJerseyState = signal<boolean>(false);
  companyId = signal<number | null>(null);
  slaNumbersList = signal<any[]>([]);
  existingNJTNList = signal<any[]>([]); // For uniqueness validation

  // Effect to automatically populate premises addresses when data becomes available
  private premisesDataEffect = effect(() => {
    const addressList = this.premisesAddressList();
    // Only populate if we have data and there are loss payees
    if (addressList && addressList.length > 0 && this.lossPayeeItems.length > 0) {
      // Use setTimeout to ensure this runs after Angular processes form changes
      setTimeout(() => {
        this.populatePremisesAddressesForExistingLossPayees();
      }, 0);
    }
  });

  shouldShowDays(): boolean {
    const start = this.bindIssuePolicyForm.get('policyStartDate')?.value;
    const end = this.bindIssuePolicyForm.get('PolicyEndDate')?.value;
    return !!start && !!end && (this.daysDifference ?? 0) > 0;
  }

  private computePolicyPeriodMonthsAndDays():
    | { months: number; days: number }
    | null {
    const startRaw = this.bindIssuePolicyForm.get('policyStartDate')?.value;
    const endRaw = this.bindIssuePolicyForm.get('PolicyEndDate')?.value;
    if (!startRaw || !endRaw) return null;

    const startDate = moment(startRaw, 'YYYY-MM-DD', true);
    const endDate = moment(endRaw, 'YYYY-MM-DD', true);
    if (!startDate.isValid() || !endDate.isValid()) return null;

    let months = endDate.diff(startDate, 'months');
    let anchor = startDate.clone().add(months, 'months');
    let days = endDate.diff(anchor, 'days');

    if (days < 0) {
      months = months - 1;
      anchor = startDate.clone().add(months, 'months');
      days = endDate.diff(anchor, 'days');
    }

    return { months, days };
  }

  private syncPolicyPeriodFromDates(): void {
    const period = this.computePolicyPeriodMonthsAndDays();
    if (!period) {
      this.daysDifference = 0;
      return;
    }

    this.bindIssuePolicyForm.patchValue({ monthsDifference: String(period.months) }, { emitEvent: false });
    this.daysDifference = period.days;
  }

  ngOnInit(): void {
    this.authService.waitForSessionCachesRestored().subscribe(() => {
      this.initializeAfterCacheHydration();
    });
  }

  private initializeAfterCacheHydration(): void {
      const irStatus = String(sessionStorage.getItem("inceptionStatus"))
                  const isReferral = Number(sessionStorage.getItem("isreferral"))
                  if(irStatus === "IDR" && isReferral === 1){
                    this.isInceptionReferralLock.set(true);         
                  }else{
                    this.isInceptionReferralLock.set(false);
                  } 
    this.navLogoClickSubscription = this.navigationHandler.logoClick$.subscribe(() => {
      this.onSaveIndicationHomeAction();
    });
    this.setDateLimits();
      this.onGetSystemUserMessage();
    this.targetPremiumEmitter.emit(true);
    this.getInceptionStatusList();
    this.loadSurplusLinesTaxFees();
    this.setupSurplusOtherValidation();
    this.focusInceptionDateField();
    
    if(this.authService.isPolicyType()){
      this.fetchBindIssuePolicyDetailsByPolicyId(Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId), Number(this.authService.getUserDetailsFromStorage()?.policyId), Number(this.authService.getUserDetailsFromStorage()?.companyId));
      this.enableViewAndDownloadDocumentSignal.set(true);
      this.checkDownloadEmailCompleteStatus();
      const isReviewProcess = JSON.parse(sessionStorage.getItem('isReview') || 'false');
      isReviewProcess === true ? this.isReadOnly.set(true) : this.isReadOnly.set(false);
    }else{
      this.fetchQuoteClearanceData();
    }  
     
  }
formatDate(date: Date): string {
  return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
}

  private applySurplusOtherValidator(): void {
    const surplusNumber = this.bindIssuePolicyForm.get('surplusNumber')?.value;
    const surplusOtherControl = this.bindIssuePolicyForm.get('surplusOther');
    if (!surplusOtherControl) {
      return;
    }
    if (surplusNumber === 'Others') {
      surplusOtherControl.setValidators([Validators.required, noWhitespaceValidator]);
    } else {
      surplusOtherControl.clearValidators();
      surplusOtherControl.setValue('', { emitEvent: false });
    }
    surplusOtherControl.updateValueAndValidity({ emitEvent: false });
  }

  private setupSurplusOtherValidation(): void {
    this.bindIssuePolicyForm.get('surplusNumber')?.valueChanges.subscribe(() => {
      this.applySurplusOtherValidator();
    });
    this.applySurplusOtherValidator();
  }

  fetchQuoteClearanceData() {
    this.isLoading.set(true);
    const quoteId = this.authService.getUserDetailsFromStorage().quoteId ;
    this.propertyService.getQuoteClearanceData(Number(quoteId)).subscribe({
      next: (response: clearanceQuoteData) => {
        if (response) {
          
          const { quoteReferralLock,quoteNo,quoteVersionBound,quoteId } = response.quote;
          const quoteDate = this.formatDate(response.quote.quoteDate);         
            if (typeof sessionStorage !== "undefined") {           
                sessionStorage.setItem("quoteReferralLock",String(quoteReferralLock));
                sessionStorage.setItem("quoteNo",String(quoteNo));
                sessionStorage.setItem("quoteDate",quoteDate);   
                sessionStorage.setItem("quoteId",String(quoteId));
                sessionStorage.setItem("quoteVersionBound",String(quoteVersionBound)) ;             
            }
            this.GetQuoteDateDetails();
            // const quoteId = response.quote.quoteId;
            const selectedQuoteVersionFromStorage = this.indicationQuoteService.getSelectedQuoteVersion();
            const quoteVersionBoundvalue = response.quote.quoteVersionBound;

                if(selectedQuoteVersionFromStorage || quoteVersionBoundvalue || response.quote.quoteId){
                  this.selectedQuote.set(selectedQuoteVersionFromStorage);
                  const userId = this.authService.getUserDetailsFromStorage()?.userDetails.userId;
                  const quoteId = selectedQuoteVersionFromStorage?.quoteId ? selectedQuoteVersionFromStorage?.quoteId : response.quote.quoteId;
                  const versionNo = this.isInceptionReferralLock() ? quoteVersionBoundvalue : (selectedQuoteVersionFromStorage?.versionNo ? selectedQuoteVersionFromStorage?.versionNo : quoteVersionBoundvalue) 
                  const userDetails = this.authService.getUserDetailsFromStorage();
                  const companyId = userDetails?.companyId;
                  // const versionNo = selectedQuoteVersionFromStorage?.versionNo ? selectedQuoteVersionFromStorage?.versionNo : quoteVersionBoundvalue;
                  this.fetchBindIssuePolicyDetails(Number(userId), Number(quoteId), Number(versionNo), Number(companyId));                  
                  this.loadQuoteEQBDCoverageStatus();
                }else{
                  this.isLoading.set(false);
                }
                      
        }
      },
      error: (error) => {
         this.isLoading.set(false);
        this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    });
  }

  private loadQuoteEQBDCoverageStatus(): void {
    if (this.authService.isPolicyType()) {
      return;
    }

    const quoteId = Number(this.authService.getUserDetailsFromStorage()?.quoteId);
    const quoteVersionBound = Number(sessionStorage.getItem("quoteVersionBound"));
    const versionId = Number(this.selectedQuote()?.versionNo) ? Number(this.selectedQuote()?.versionNo) : quoteVersionBound;
    if (!quoteId || !versionId) {
      return;
    }

    this.indicationQuoteService.GetStep4DQuoteEQBD(quoteId, versionId).subscribe({
      next: (response: any) => {
        const data = response?.data ?? response;
        const isEQBDAvailable = Number(data?.isEQBDAvailable ?? 1);
        const bbpLimitRows = Array.isArray(data?.bbpLimit) ? data.bbpLimit : [];
        const restrictedOccupancies = new Set(
          EQBD_HIGH_BPP_RESTRICTED_DESCRIPTIONS.map((item) => String(item).trim().toLowerCase())
        );
        const hasRestrictedHighBpp = bbpLimitRows.some((item: any) => {
          const occupancy = String(item?.occupancyClass ?? item?.occupancy ?? '').trim().toLowerCase();
          const bppLimit = Number(item?.bppLimit ?? 0);
          return restrictedOccupancies.has(occupancy) && bppLimit > 499999;
        });
        const shouldDisableEqbdConfirmation = isEQBDAvailable === 0 || hasRestrictedHighBpp;

        if (shouldDisableEqbdConfirmation) {
          this.isEqbdConfirmationDisabled.set(true);
          // When disabled, EQBD must be rejected regardless of saved confirmation flags.
          this.bindIssuePolicyForm.patchValue({ eqbdAcceptance: 0 }, { emitEvent: false });
          this.bindIssuePolicyForm.get('eqbdAcceptance')?.disable({ emitEvent: false });
        } else {
          this.isEqbdConfirmationDisabled.set(false);
          this.bindIssuePolicyForm.get('eqbdAcceptance')?.enable({ emitEvent: false });
        }
      },
      error: () => {
        this.isEqbdConfirmationDisabled.set(false);
      }
    });
  }

  GetQuoteDateDetails(){
    const quoteNo = String(sessionStorage.getItem("quoteNo"));

    this.indicationQuoteService.GetQuoteDateDetails(quoteNo).subscribe({
      next :(response:any) => {
        if (response && Array.isArray(response)) {
          // Get quoteVersionBound from session storage
          const quoteVersionBound = sessionStorage.getItem("quoteVersionBound");
          const selectedQuoteVersionFromStorage = this.indicationQuoteService.getSelectedQuoteVersion();

          const versionNo = selectedQuoteVersionFromStorage?.versionNo ? selectedQuoteVersionFromStorage?.versionNo : quoteVersionBound;

          
          if (versionNo) {
            // Find the matching version in the response
            const matchingVersion = response.find(item => 
              item.versionNo === Number(versionNo)
            );
            
            
            if (matchingVersion) {
              // Set the corresponding quote date in session storage
              sessionStorage.setItem("quoteDate", matchingVersion.quoteDate);
              
            }
          }
        }
      },
      error:(error:any) => {

      }
    })
  }
  isOtherSelected(): boolean {
  return this.bindIssuePolicyForm.get('surplusNumber')?.value === 'Others';
}


  formatPhoneNumberBind(phone: string | number | null | undefined): string {
    if (phone == null) { // Handles both null and undefined
        return ''; // Or return a default value like 'N/A' or 'Unknown'
    }

    const phoneStr = phone.toString();
    if (phoneStr.length === 10) {
        return `(${phoneStr.substring(0, 3)}) ${phoneStr.substring(3, 6)}-${phoneStr.substring(6, 10)}`;
    }
    return phoneStr; // Return as is if it's not a 10-digit number
}


  formatPhoneNumber(event: any) {
    let input = event.target.value.replace(/\D/g, ''); // Remove all non-digit characters
    if (input.length > 10) {
      input = input.substring(0, 10); // Trim to 10 digits
    }

    let formattedPhone = '';
    if (input.length > 0) {
      formattedPhone = `(${input.substring(0, 3)}`;

    }
    if (input.length >= 4) {
      formattedPhone += `) ${input.substring(3, 6)}`;

    }
    if (input.length >= 7) {
      formattedPhone += `-${input.substring(6, 10)}`;

    }

    this.bindIssuePolicyForm.get('producingAgentPhone')?.setValue(formattedPhone, { emitEvent: false });
  }

   formatInsuredPhoneNumber(event: any) {
    let input = event.target.value.replace(/\D/g, ''); // Remove all non-digit characters
    if (input.length > 10) {
      input = input.substring(0, 10); // Trim to 10 digits
    }

    let formattedInsuredPhoneNumber = ''
    if (input.length > 0) {
      formattedInsuredPhoneNumber = `(${input.substring(0, 3)}`;

    }
    if (input.length >= 4) {
      formattedInsuredPhoneNumber += `) ${input.substring(3, 6)}`;

    }
    if (input.length >= 7) {
      formattedInsuredPhoneNumber += `-${input.substring(6, 10)}`;

    }

    this.bindIssuePolicyForm.get('InsuredPhoneNumber')?.setValue(formattedInsuredPhoneNumber, {emitEvent : false})
  }

  fetchBindIssuePolicyDetails(userId: number, quoteId: number, versionId: number, companyId?: number) {
    forkJoin([
      this.indicationQuoteService.fetchBindIssuePolicyDetails(quoteId, userId, versionId),
      this.indicationQuoteService.getBindIssuePolicyClearanceAddress(quoteId,versionId),
      this.indicationQuoteService.getBindIssuePolicyLossPayeeType(),
      this.indicationQuoteService.GetSLLicenseNumberByState(quoteId, 'Quote', companyId),
      this._service.fetchPolicyParameterQuoteOandL(quoteId,versionId),
      this._service.getStates()
    ]).pipe(
      map(([bindIssuePolicyDetails, clearanceAddressList, lossPayeeTypeList, SLLicenseTypeList, premisesAddressList, statesList]) => {
        return {bindIssuePolicyDetails, clearanceAddressList, lossPayeeTypeList, SLLicenseTypeList,premisesAddressList, statesList};
      })
    ).subscribe({
      next: (response: any) => {
        const {bindIssuePolicyDetails, clearanceAddressList, lossPayeeTypeList,SLLicenseTypeList, premisesAddressList, statesList} = response;
        const slLicenseItems = Array.isArray(SLLicenseTypeList)
          ? SLLicenseTypeList
          : (Array.isArray(SLLicenseTypeList?.data) ? SLLicenseTypeList.data : []);
        if(bindIssuePolicyDetails?.result && clearanceAddressList?.result && lossPayeeTypeList && premisesAddressList && statesList){
          this.clearanceAddress.set(clearanceAddressList?.result);
          this.lossPayeeTypeList.set(lossPayeeTypeList);
          const filteredList = slLicenseItems.filter((item:any) => item !== null);
          filteredList.push('Others');
          this.SLlicenceNumberList.set(filteredList);
          this.masterStateList.set(statesList); // state list
          const updatedAddressList = this._service.updateToBuildingListByAddressId(premisesAddressList); // transform addressList by BuildingId
          
          this.premisesAddressList.set(updatedAddressList);
          
          // Check if primary address is New Jersey
          this.checkIfNewJerseyState(clearanceAddressList?.result);
          
          this.onBindFormData(bindIssuePolicyDetails?.result, updatedAddressList);
          // Populate premises addresses for any loss payees that were added before data was loaded
          this.populatePremisesAddressesForExistingLossPayees();
        }
      },
      error: (error: any) => {
        this.isLoading.set(false);
        this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    })
  }


  fetchBindIssuePolicyDetailsByPolicyId(userId: number, policyId: number,companyId?: number) {
    this.isLoading.set(true);
    forkJoin([
      this.indicationQuoteService.fetchBindIssuePolicyDetailsByPolicyId(policyId, userId),
      this.indicationQuoteService.getClearanceAddressForPolicy(policyId),
      this.indicationQuoteService.getBindIssuePolicyLossPayeeType(),
      this.indicationQuoteService.GetSLLicenseNumberByState(policyId, 'Policy', companyId),
      this._service.getPolicyOandLDetails(policyId),
      this._service.getStates()
    ]).pipe(
      map(([bindIssuePolicyDetails, clearanceAddressList, lossPayeeTypeList,SLLicenseTypeList, premisesAddressList, statesList]) => {
        return {bindIssuePolicyDetails, clearanceAddressList, lossPayeeTypeList,SLLicenseTypeList, premisesAddressList, statesList};
      })
    ).subscribe({
      next: (response: any) => {
        const {bindIssuePolicyDetails, clearanceAddressList, lossPayeeTypeList,SLLicenseTypeList, premisesAddressList, statesList} = response;
        const slLicenseItems = Array.isArray(SLLicenseTypeList)
          ? SLLicenseTypeList
          : (Array.isArray(SLLicenseTypeList?.data) ? SLLicenseTypeList.data : []);
        if(bindIssuePolicyDetails?.result && clearanceAddressList?.result && lossPayeeTypeList && premisesAddressList && statesList){
          this.clearanceAddress.set(clearanceAddressList?.result);
          this.lossPayeeTypeList.set(lossPayeeTypeList);
          this.masterStateList.set(statesList); // state list                    
          const updatedAddressList = this._service.updateToBuildingListByAddressId(premisesAddressList); // transform addressList by BuildingId
          this.premisesAddressList.set(updatedAddressList);          
          const filteredList = slLicenseItems.filter((item:any) => item !== null);
          filteredList.push('Others');
          // filteredList.push(bindIssuePolicyDetails?.result.surplusNumber)
          this.SLlicenceNumberList.set(filteredList);
          
          // Check if primary address is New Jersey
          this.checkIfNewJerseyState(clearanceAddressList?.result);
          
          this.onBindFormData(bindIssuePolicyDetails?.result, updatedAddressList);
          // Populate premises addresses for any loss payees that were added before data was loaded
          this.populatePremisesAddressesForExistingLossPayees();
        }
      },
      error: (error: any) => {
        this.isLoading.set(false);
        this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    })
  }

  setDateLimits(){
      const mindate = Number(sessionStorage.getItem("inDtMinLimit"));
      const maxdate = Number(sessionStorage.getItem("inDtMaxLimit"));

     const today = moment();
     this.minStartDate = today.clone().subtract(mindate, 'days').format('YYYY-MM-DD');
     this.maxStartDate = today.clone().add(maxdate, 'days').format('YYYY-MM-DD');
  }

  private focusInceptionDateField(): void {
    setTimeout(() => {
      if (!this.policyStartDateInput || this.isReadOnly() || this.isInceptionReferralLock()) {
        return;
      }
      this.policyStartDateInput.nativeElement.focus();
    }, 0);
  }

  getInceptionDateInlineMessage(): string {
    return "Please review the proposed inception Date. To proceed with system binding, the inception date must be within the last 4 days. For inception dates prior to this, please complete bind step and submit referral";
  }

  private showInceptionDateValidationToast(): void {
    this.showInceptionDateInlineMessage.set(true);
    this.toastService.addNotification({
      message: this.getInceptionDateInlineMessage(),
      type: ToastType.Error
    });
  }

  private isInceptionDateOutsideAutoBindWindow(startDate: moment.Moment): boolean {
    const minInceptionValue = Number(sessionStorage.getItem("inDtValidLimit"));
    const minValidInceptionDate = moment().subtract(minInceptionValue, 'days').startOf('day');
    const quoteDateString = sessionStorage.getItem('quoteDate');
    const quoteDate = quoteDateString ? moment(quoteDateString).startOf('day') : null;
    return startDate.isBefore(minValidInceptionDate) || (!!quoteDate && startDate.isBefore(quoteDate));
  }

  onStartDateBlur(): void {

    const control = this.bindIssuePolicyForm.get('policyStartDate');
    control?.markAsTouched();
    const currentValue = String(control?.value ?? '');
    if (!currentValue) {
      this.showInceptionDateInlineMessage.set(false);
      return;
    }
    const startDate = moment(currentValue, 'YYYY-MM-DD');
    if (!startDate.isValid()) {
      this.showInceptionDateInlineMessage.set(false);
      return;
    }
    const today = moment().startOf('day');
        const status = sessionStorage.getItem("statusRagCall");

    if(status){
    if(status !== "Bind – Referral Submitted" && status !== "Bind – Referral Approved" && status !== "Policy - Complete"){
    // Same referral / inline-message rules as onStartDateChange
    if (startDate.isAfter(today)) {
      this.submitReferralSignal.set(false);
      this.showInceptionDateInlineMessage.set(false);
      return;
    }

    if (this.isInceptionDateOutsideAutoBindWindow(startDate)) {
      this.submitReferralSignal.set(true);
      this.showInceptionDateInlineMessage.set(true);
      const maxEndDate = startDate.clone().add(18, 'months');
      this.policyEndDateMax.set(maxEndDate.format('YYYY-MM-DD'));

      const endDate = startDate.clone().add(1, 'year');
      this.bindIssuePolicyForm.patchValue({ 'PolicyEndDate': endDate.format('YYYY-MM-DD') });

      this.policyEndDateMin.set(startDate.clone().add(6, 'months').format('YYYY-MM-DD'));

      const monthsDifference = endDate.diff(startDate, 'months');
      this.bindIssuePolicyForm.patchValue({ 'monthsDifference': String(monthsDifference) });
      this.daysDifference = 0;
      return;
    }

    this.submitReferralSignal.set(false);
    this.showInceptionDateInlineMessage.set(false);
  }
}
  }

  onStartDateChange(): void {
  const startDate = moment(this.bindIssuePolicyForm.get('policyStartDate')?.value, 'YYYY-MM-DD');
  const today = moment().startOf('day');
  
  // Update NJTN year when inception date changes (only if not already set from existing data)
  if (this.isNewJerseyState()) {
    setTimeout(() => {
      const currentYear = this.bindIssuePolicyForm.get('njtnYear')?.value;
      if (!currentYear) {
        this.setDefaultYear();
      }
    }, 0);
  }

  // Condition 1: If startDate is after today — no issue
  if (startDate.isAfter(today)) {
    // Proceed with normal processing
    this.submitReferralSignal.set(false);
    this.showInceptionDateInlineMessage.set(false);
  } else {
    // Condition 2: startDate is in the past — check further
    if (this.isInceptionDateOutsideAutoBindWindow(startDate)) {
      this.submitReferralSignal.set(true);
      this.showInceptionDateInlineMessage.set(true);
      const maxEndDate = startDate.clone().add(18, 'months');
      this.policyEndDateMax.set(maxEndDate.format('YYYY-MM-DD'));

      const endDate = startDate.clone().add(1, 'year');
      this.bindIssuePolicyForm.patchValue({ 'PolicyEndDate': endDate.format('YYYY-MM-DD') });

      this.policyEndDateMin.set(startDate.clone().add(6, 'months').format('YYYY-MM-DD'));

      const monthsDifference = endDate.diff(startDate, 'months');
      this.bindIssuePolicyForm.patchValue({ 'monthsDifference': String(monthsDifference) });
      this.daysDifference = 0;
      return;
    }else{
      this.submitReferralSignal.set(false);
      this.showInceptionDateInlineMessage.set(false);
    }
  }

  // All checks passed — proceed with calculations

  const maxEndDate = startDate.clone().add(18, 'months');
  this.policyEndDateMax.set(maxEndDate.format('YYYY-MM-DD'));

  const endDate = startDate.clone().add(1, 'year');
  this.bindIssuePolicyForm.patchValue({ 'PolicyEndDate': endDate.format('YYYY-MM-DD') });

  this.policyEndDateMin.set(startDate.clone().add(6, 'months').format('YYYY-MM-DD'));

  const monthsDifference = endDate.diff(startDate, 'months');
  this.bindIssuePolicyForm.patchValue({ 'monthsDifference': String(monthsDifference) });
  this.daysDifference = 0;
}


  onEndDateChange(): void {
    // Use moment.js to parse the end date and start date
    const startDate = moment(this.bindIssuePolicyForm.get('policyStartDate')?.value);
    const endDate = moment(this.bindIssuePolicyForm.get('PolicyEndDate')?.value);

    // Calculate months + days (days shown only when non-zero)
    this.syncPolicyPeriodFromDates();
  }

  onMonthsDifferenceChange(): void {
    // Use moment.js to calculate the end date based on the selected months difference
    const startDate = moment(this.bindIssuePolicyForm.get('policyStartDate')?.value);
    const endDate = startDate.clone().add(this.bindIssuePolicyForm.get('monthsDifference')?.value, 'months');

    // Set the end date in the form
    this.bindIssuePolicyForm.patchValue({'PolicyEndDate': endDate.format('YYYY-MM-DD')})
    this.daysDifference = 0;
  }

  setMonthDifferenceChange(policyStartDate:string,policyEnddate:string){
    const startDate = moment(policyStartDate);
    const endDate = moment(policyEnddate);

    const monthsDifference = endDate.diff(startDate, 'months'); // whole months
    this.bindIssuePolicyForm.patchValue({'monthsDifference': String(monthsDifference)}, { emitEvent: false });

    const anchor = startDate.clone().add(monthsDifference, 'months');
    this.daysDifference = endDate.diff(anchor, 'days');
    if (this.daysDifference < 0) {
      const correctedMonths = monthsDifference - 1;
      const correctedAnchor = startDate.clone().add(correctedMonths, 'months');
      this.bindIssuePolicyForm.patchValue({ monthsDifference: String(correctedMonths) }, { emitEvent: false });
      this.daysDifference = endDate.diff(correctedAnchor, 'days');
    }
  }

  // onTriaChange(value: number) {
    // const msg = value === 1 ? "Please upload TRIA Acceptance notice in the Uploading Center" :
    // "Please upload TRIA Declinature notice in the Uploading Center"
    // this.toastService.addNotification({message: msg, type: ToastType.Info}); // notification
  // }

  onTriaChange(value: number) {
    const currentValue = this.bindIssuePolicyForm.get('triaAcceptance')?.value;
    this.bindIssuePolicyForm.get('triaAcceptance')?.setValue(currentValue === value ? null : value);
    this.fetchEqbdTriaStatusAndBindFees();
  }

  onEqbdChange(value: number) {
    const currentValue = this.bindIssuePolicyForm.get('eqbdAcceptance')?.value;
    this.bindIssuePolicyForm.get('eqbdAcceptance')?.setValue(currentValue === value ? null : value);
    this.fetchEqbdTriaStatusAndBindFees();
  }

  private bindPolicyPremiumDetails(source: any): void {
    const hasCommercialProperty = source?.commercialProperty !== null && source?.commercialProperty !== undefined;
    const hasEqbdPremium = source?.eqbdPremium !== null && source?.eqbdPremium !== undefined;
    const hasTriaPremium = source?.triaPremium !== null && source?.triaPremium !== undefined;
    const inspectionFee = source?.inspectionFee !== null && source?.inspectionFee !== undefined;

    if (hasCommercialProperty) {
      const commercialProperty = Number(source.commercialProperty);
      this.commercialPropertyPremium.set(isNaN(commercialProperty) ? 0 : commercialProperty);
    }
    if (hasEqbdPremium) {
      const eqbdPremium = Number(source.eqbdPremium);
      this.eqbdPremium.set(isNaN(eqbdPremium) ? 0 : eqbdPremium);
    }
    if (hasTriaPremium) {
      const triaPremium = Number(source.triaPremium);
      this.triaPremium.set(isNaN(triaPremium) ? 0 : triaPremium);
    }

    if(inspectionFee){
      const inspectionFeeAmount = Number(source.inspectionFee);
      this.inspectionFeePremium.set(isNaN(inspectionFeeAmount) ? 0 : inspectionFeeAmount);
    }


  }

  isTriaCoverageNotSelected(): boolean {
    const triaSelection = this.bindIssuePolicyForm.get('triaAcceptance')?.value;
    return triaSelection === null || triaSelection === undefined;
  }

  getPolicyTriaPremiumAmount(): number {
    if (this.isTriaCoverageNotSelected()) {
      return 0;
    }
    const triaSelection = this.bindIssuePolicyForm.get('triaAcceptance')?.value;
    return Number(triaSelection) === 1 ? this.triaPremium() : 0;
  }

  getPolicyEqbdPremiumAmount(): number {
    const eqbdSelection = this.bindIssuePolicyForm.get('eqbdAcceptance')?.value;
    return Number(eqbdSelection) === 1 ? this.eqbdPremium() : 0;
  }

  getPolicyPremiumTotal(): number {
    let additionalSlSum = 0;
    this.slTaxFeeItems.controls.forEach((ctrl) => {
      const feeAmount = (ctrl as FormGroup).get('feeAmount')?.value;
      additionalSlSum += this.parseAmount(feeAmount);
    });
    return (
      this.commercialPropertyPremium()
      + this.getPolicyEqbdPremiumAmount()
      + this.getPolicyTriaPremiumAmount()
      + this.inspectionFeePremium()
      + this.parseAmount(this.bindIssuePolicyForm.get('policyFee')?.value)
      + this.parseAmount(this.bindIssuePolicyForm.get('surplusLinesTax')?.value)
      + additionalSlSum
    );
  }

  private fetchEqbdTriaStatusAndBindFees(): void {
    if (this.authService.isPolicyType()) {
      return;
    }

    const triaSelection = this.bindIssuePolicyForm.get('triaAcceptance')?.value;
    const eqbdSelection = this.bindIssuePolicyForm.get('eqbdAcceptance')?.value;
    if (triaSelection === null || triaSelection === undefined || eqbdSelection === null || eqbdSelection === undefined) {
      return;
    }

    const quoteId = Number(this.authService.getUserDetailsFromStorage()?.quoteId);
    if (!quoteId) {
      return;
    }
    const quoteVersionBound = Number(sessionStorage.getItem("quoteVersionBound"));
    const versionId = Number(this.selectedQuote()?.versionNo) ? Number(this.selectedQuote()?.versionNo) : quoteVersionBound;
    
    if (!versionId) {
      return;
    }

    const tria = Number(triaSelection) === 1;
    const eqbd = Number(eqbdSelection) === 1;

    this.indicationQuoteService.GetEqbdTriaStatus(quoteId, eqbd, tria, versionId).subscribe({
      next: (response: any) => {
        const responseData = response?.data ?? response;
        this.bindPolicyPremiumDetails(responseData);
        this.setSurplusFeeDetails(responseData?.slTaxFeeDetails || response?.slTaxFeeDetails || []);
      },
      error: () => {}
    });
  }

  defaultPolicyEndData(policyStartDate: string) {
    const startDate = moment(policyStartDate);
    const maxEndDate = startDate.clone().add(18, 'months');

    this.policyEndDateMin.set(startDate.clone().add(6, 'months').format('YYYY-MM-DD'));
    this.policyEndDateMax.set(maxEndDate.format('YYYY-MM-DD'));
    
  }

formatPolicyDate(policyStartDate: string | moment.Moment): string {
    return moment(policyStartDate).format('YYYY-MM-DD');
  }

  onBindFormInitially(bindIssuePolicyData: any, premisesAddressList: any[]){
    const {policyStartDate,policyEndDate} = bindIssuePolicyData;

    this.bindIssuePolicyForm.patchValue({
      policyStartDate: this.formatPolicyDate(policyStartDate), PolicyEndDate:this.formatPolicyDate(policyEndDate)
    });
    this.initialPolicyStartDateValue = String(this.bindIssuePolicyForm.get('policyStartDate')?.value ?? '');
    this.defaultPolicyEndData(policyStartDate)
    this.setMonthDifferenceChange(policyStartDate,policyEndDate)

    
  }

   onBindFormData(bindIssuePolicyData: any, premisesAddressList: any[]){
    const {policyStartDate, policyEndDate, triaAcceptance, lossPayees, productingAgentCompanyName,productingAgentName, productingAgentEmail, producingAgentPhone,InsuredPhoneNumber, producingAgentAddress,
      producerName, surplusNumber, slAddress, insuredContactName, insuredEmailAddress, insuredMailingAddress
    } = bindIssuePolicyData;    
    const eqbdConfirmed = bindIssuePolicyData?.IsEQBDConfirmed ?? bindIssuePolicyData?.isEQBDConfirmed ?? null;
    const normalizedEqbdConfirmed = eqbdConfirmed === null || eqbdConfirmed === undefined || eqbdConfirmed === '' ? null : Number(eqbdConfirmed);
    let eqbdSelection: number | null = null;
    if (this.isEqbdConfirmationDisabled()) {
      // Always reject when confirmation is disabled.
      eqbdSelection = 0;
    } else if (normalizedEqbdConfirmed === 1) {
      eqbdSelection = 1;
    } else if (normalizedEqbdConfirmed === 0) {
      eqbdSelection = 0;
    }

    const formattedPhone = bindIssuePolicyData.producingAgentPhone != null  // Check for both null and undefined
        ? this.formatPhoneNumberBind(bindIssuePolicyData.producingAgentPhone)
        : '';
    const formattedInsuredPhoneNumber = bindIssuePolicyData.insuredPhoneNumber != null
    ? this.formatPhoneNumberBind(bindIssuePolicyData.insuredPhoneNumber)
    : '';
      const matchFound = this.SLlicenceNumberList().includes(surplusNumber);
      if(surplusNumber){
        if (matchFound) {
          this.bindIssuePolicyForm.patchValue({ surplusNumber: surplusNumber });
        } else {
          this.bindIssuePolicyForm.patchValue({ surplusNumber: 'Others', surplusOther: surplusNumber });
        }
    }
    this.applySurplusOtherValidator();

    
    const today = moment();
    const incomingPolicyStartDate = moment(policyStartDate);
    const incomingPolicyEndDate = moment(policyEndDate);
    let finalPolicyStartDate = incomingPolicyStartDate;
    let finalPolicyEndDate = incomingPolicyEndDate

    const minInceptionValue = Number(sessionStorage.getItem("inDtValidLimit"));

    const status = sessionStorage.getItem("statusRagCall");

    if(status){
    if(status !== "Bind – Referral Submitted" && status !== "Bind – Referral Approved" && status !== "Policy - Complete"){
    if (incomingPolicyStartDate.isBefore(today, 'day')) {
      const minValidInceptionDate = moment().subtract(minInceptionValue, 'days').startOf('day');

    // Get quote date from session
    
    const quoteDateString = sessionStorage.getItem('quoteDate');
  
    const quoteDate = quoteDateString ? moment(quoteDateString).startOf('day') : null;

    // Condition 2: startDate is in the past — check further
    if (
 incomingPolicyStartDate.isBefore(minValidInceptionDate) || 
      (quoteDate && incomingPolicyStartDate.isBefore(quoteDate))    ) {
      this.submitReferralSignal.set(true);
      this.showInceptionDateValidationToast();
       
      }else{
       this.submitReferralSignal.set(false);
      }

      const calculatedEndDate = finalPolicyStartDate.clone().add(1, 'year'); // Inclusive of start date
      // finalPolicyEndDate = calculatedEndDate;
    }else{
      this.submitReferralSignal.set(false);
    }
  }
}
this.isLoading.set(false);
    this.bindIssuePolicyForm.patchValue({
      policyStartDate: this.formatPolicyDate(finalPolicyStartDate), PolicyEndDate:this.formatPolicyDate(finalPolicyEndDate),ProductingAgentCompanyName: productingAgentCompanyName, productingAgentName: productingAgentName, productingAgentEmail: productingAgentEmail,
      producingAgentPhone: formattedPhone,InsuredPhoneNumber:formattedInsuredPhoneNumber, producerName: producerName,  insuredContactName: insuredContactName, 
      insuredEmailAddress: insuredEmailAddress, triaAcceptance: triaAcceptance, eqbdAcceptance: eqbdSelection
    });
    this.bindPolicyPremiumDetails(bindIssuePolicyData);
    // this.fetchEqbdTriaStatusAndBindFees();
    this.initialPolicyStartDateValue = String(this.bindIssuePolicyForm.get('policyStartDate')?.value ?? '');

    
    this.defaultPolicyEndData(policyStartDate)
    this.setMonthDifferenceChange(policyStartDate,policyEndDate)
    this.focusInceptionDateField();
    
    // Set default year for NJTN if New Jersey state is detected
    if (this.isNewJerseyState()) {
      this.setDefaultYear();
    }
    
    this.bindIssuePolicyForm.get('producingAgentAddress')?.patchValue({
      unitNum: producingAgentAddress?.unitNum,
      streetNum: producingAgentAddress?.streetNum,
      streetName: producingAgentAddress?.streetName,
      cityId: producingAgentAddress?.cityId,
      stateId: producingAgentAddress?.stateId,
      countyId: producingAgentAddress?.countyId,
      zipCode: producingAgentAddress?.zipCode,
      quoteAddressId: producingAgentAddress?.quoteAddressId,
      addressTypeId: producingAgentAddress?.addressTypeId,
      quoteId: producingAgentAddress?.quoteId
    });
    this.bindIssuePolicyForm.get('slAddress')?.patchValue({
      unitNum: slAddress?.unitNum,
      streetNum: slAddress?.streetNum,
      streetName: slAddress?.streetName,
      cityId: slAddress?.cityId,
      stateId: slAddress?.stateId,
      countyId: slAddress?.countyId,
      zipCode: slAddress?.zipCode,
      quoteAddressId: slAddress?.quoteAddressId,
      addressTypeId: slAddress?.addressTypeId,
      quoteId: slAddress?.quoteId
    });
    this.bindIssuePolicyForm.get('insuredMailingAddress')?.patchValue({
      unitNum: insuredMailingAddress?.unitNum,
      streetNum: insuredMailingAddress?.streetNum,
      streetName: insuredMailingAddress?.streetName,
      cityId: insuredMailingAddress?.cityId,
      stateId: insuredMailingAddress?.stateId,
      countyId: insuredMailingAddress?.countyId,
      zipCode: insuredMailingAddress?.zipCode,
      quoteAddressId: insuredMailingAddress?.quoteAddressId,
      addressTypeId: insuredMailingAddress?.addressTypeId,
      quoteId: insuredMailingAddress?.quoteId
    });
    if (producingAgentAddress?.zipCode) {
      this.fetchStateByZipCode(producingAgentAddress.zipCode, "PA"); // for Producing Agent state, county and city
    }
    if (slAddress?.zipCode) {
      this.fetchStateByZipCode(slAddress.zipCode, "SI"); // for Surplus Information state, county and city
    }
    if (insuredMailingAddress?.zipCode) {
      this.fetchStateByZipCode(insuredMailingAddress.zipCode, "PI"); // for Postbind Inspection state, county and city
    }

   

    if(lossPayees){
       this.bindIssuePolicyForm.get('termsAndConditions')?.patchValue({
        acceptTerms: this.authService.isPolicyType(),
        materialFacts: this.authService.isPolicyType(),
        slTaxNotice: this.authService.isPolicyType()
      });
      this.onBindLossPayeeFormData(lossPayees, premisesAddressList)
    }
    this.setSurplusFeeDetails(bindIssuePolicyData?.bislFeeDetails || []);
  this.setDateLimits();
  
     
  }

  onBindLossPayeeFormData(lossPayees: any[], premisesAddressList: any[]){
    const lossPayeeArray = this.bindIssuePolicyForm.get('lossPayeeDetailsForm') as FormArray;
    lossPayeeArray.clear(); // Clear existing controls

    lossPayees?.forEach((payee: any, index: number) => {
      const payeeGroup = this.fb.group({
        lossPayeeId: payee?.lossPayeeId,
        lossPayeeName: payee?.lossPayeeName,
        quoteId: payee?.quoteId,
        payeeApplicableClause: payee?.payeeApplicableClause,
        unitNum: payee?.payeeAddress?.unitNum,
        streetNum: payee?.payeeAddress?.streetNum,
        streetName: payee?.payeeAddress?.streetName,
        cityId: [payee?.payeeAddress?.cityId, [Validators.required, noWhitespaceValidator]],
        stateId: [payee?.payeeAddress?.stateId, [Validators.required, noWhitespaceValidator]],
        countyId: [payee?.payeeAddress?.countyId, [Validators.required, noWhitespaceValidator]],
        zipCode: [payee?.payeeAddress?.zipCode, [Validators.required, noWhitespaceValidator]],
        quoteAddressId: payee?.payeeAddress?.quoteAddressId,
        addressTypeId: payee?.payeeAddress?.addressTypeId,
        premisesAddress: this.fb.array([]),
      });
      if (payee?.payeeAddress?.zipCode) {
        this.fetchStateByLPZipCode(index, payee.payeeAddress.zipCode); // for Loss Payee state, county and city
      }
      const premisesArray = payeeGroup.get('premisesAddress') as FormArray;
      this.setPremisesAddressData(premisesArray, premisesAddressList, payee?.premisesAddress);
      lossPayeeArray.push(payeeGroup);
    });
  }

  setPremisesAddressData(premisesArray: FormArray, addressList: any[], premisesAddressFromApi: any[]){
    const premisesFromApi = Array.isArray(premisesAddressFromApi) ? premisesAddressFromApi : [];
    addressList?.forEach((address) => {
      const addressName = `${address.streetNum} ${address.streetName} ${address.city} ${address.state} ${address.county}`
      const matchingPremises = premisesFromApi.find(
        premises => Number(premises.buildingId) === Number(address.buildingId)
      )
    
      const premisesAddressGroup = this.fb.group({
        premisesAddress: [addressName],
        premisesBuilding: [address.buildingNum],
        isPremisesChecked: [!!matchingPremises],
        lossPayeeBuildingId: [matchingPremises ? matchingPremises.lossPayeeBuildingId : 0],
        payeeId: [matchingPremises ? matchingPremises.payeeId : 0],
        quoteId: [matchingPremises ? matchingPremises.lossPayeeBuildingId : ''],
        buildingId: [address.buildingId],
        premisesAddressId: [address.addressid]
      });
      

      premisesArray.push(premisesAddressGroup);
      
    });
  }

  onAddressSelect(event: any) {
    const selectedId = event.target.value;
    const getSelectedAddress = this.clearanceAddress().filter((addr) => Number(addr.addressId) === Number(selectedId));
    if(getSelectedAddress.length > 0){
      const {addressId, unitNum, streetNum, streetName, cityId, countyId, stateId, zipcode} = getSelectedAddress[0];
      this.bindIssuePolicyForm.get('insuredMailingAddress')?.patchValue({
        unitNum: unitNum,
        streetNum: streetNum,
        streetName: streetName,
        cityId: cityId,
        stateId: stateId,
        countyId: countyId,
        zipCode: zipcode,
        quoteAddressId: 0,
        addressTypeId: addressId,
        quoteId: ''
      });

      this.fetchStateByZipCode(zipcode, "PI");
    };
  }

  onCompanyBranchCheck(e: any) {
    this.isCompanyBranchSelected = e.target.checked;
    if (!this.isCompanyBranchSelected) {
      return;
    }
    this.loadCompanyBranchAddresses();
  }

  private loadCompanyBranchAddresses(): void {
    const quoteId = Number(this.authService.getUserDetailsFromStorage()?.quoteId ?? sessionStorage.getItem('quoteId'));
    if (!quoteId) {
      this.companyBranchAddressOptions.set([]);
      return;
    }
    this.indicationQuoteService.GetCompanyBranchAddresses(quoteId).subscribe({
      next: (response: any) => {
        const branchAddress = response?.branchAddress ?? response?.data?.branchAddress;
        const companyAddress = response?.companyAddress ?? response?.data?.companyAddress;
        const options: { key: 'companyAddress' | 'branchAddress'; label: string; address: any }[] = [];
        if (companyAddress) {
          options.push({
            key: 'companyAddress',
            label: 'Company Address',
            address: companyAddress
          });
        }
        if (branchAddress) {
          options.push({
            key: 'branchAddress',
            label: 'Branch Address',
            address: branchAddress
          });
        }
        this.companyBranchAddressOptions.set(options);
      },
      error: () => {
        this.companyBranchAddressOptions.set([]);
      }
    });
  }

  onCompanyBranchAddressSelect(event: any) {
    const selectedKey = event.target.value as 'companyAddress' | 'branchAddress';
    const selected = this.companyBranchAddressOptions().find((opt) => opt.key === selectedKey);
    if (!selected?.address) {
      return;
    }
    const { addressId, unitNum, streetNum, streetName, cityId, countyId, stateId, zipCode } = selected.address;
    this.bindIssuePolicyForm.get('slAddress')?.patchValue({
      unitNum: unitNum ?? '',
      streetNum: streetNum ?? '',
      streetName: streetName ?? '',
      cityId: cityId ?? '',
      stateId: stateId ?? '',
      countyId: countyId ?? '',
      zipCode: zipCode ?? '',
      quoteAddressId: 0,
      addressTypeId: addressId ?? 0,
      quoteId: ''
    });
    if (zipCode) {
      this.fetchStateByZipCode(zipCode, "SI");
    }
  }

  setLossPayeeDetail() {
    const createLossPayee = this.fb.group({
      lossPayeeName: [''],
      payeeApplicableClause: [''],
      unitNum: [''],
      streetNum: [''],
      streetName: [''],
      cityId: ['', [Validators.required, noWhitespaceValidator]],
      stateId: ['', [Validators.required, noWhitespaceValidator]],
      countyId: ['', [Validators.required, noWhitespaceValidator]],
      zipCode: ['', [Validators.required, noWhitespaceValidator]],
      premisesAddress: this.fb.array([]),
      lossPayeeId: [''],
      quoteId: [''],
      quoteAddressId: [''],
      addressTypeId: ['']
    })
    const index = this.lossPayeeItems.length;
    this.lossPayeeItems.push(createLossPayee);

    this.states.update((map) => {map.set(index, []); return map;});
    this.counties.update((map) => {map.set(index, []); return map;});
    this.cities.update((map) =>{map.set(index, []); return map;});
    this.filteredStates.update((map) => {map.set(index, []); return map;});
    this.filteredCounty.update((map) => {map.set(index, []); return map;});
    this.filteredCity.update((map) => {map.set(index, []); return map;});
    this.searchLPState.update((map) => {map.set(index, ''); return map;});
    this.searchLPCounty.update((map) => {map.set(index, ''); return map;});
    this.searchLPCity.update((map) => {map.set(index, ''); return map;});

    const lossPayeeDetailsForm = this.bindIssuePolicyForm.get('lossPayeeDetailsForm') as FormArray;
    const newIndex = this.lossPayeeItems.length - 1;
    this.addPremisesAddress(newIndex);
    
    // If data becomes available after adding, ensure it gets populated
    // The effect will handle this, but we also try immediately in case data is already loaded
    if (this.premisesAddressList() && this.premisesAddressList().length > 0) {
      // Use setTimeout to ensure form is fully initialized
      setTimeout(() => {
        const premisesArray = this.premisesItems(newIndex);
        if (premisesArray.length === 0) {
          this.addPremisesAddress(newIndex);
        }
      }, 0);
    }
  }

  allowOnlyNumbers(event : KeyboardEvent) : void{
    const charCode = event.key.charCodeAt(0);
    if (charCode < 48 || charCode > 57) {
        event.preventDefault();
    }
  }

  allowCurrencyInput(event: KeyboardEvent): void {
    const key = event.key;
    const allowedPattern = /^[0-9.]$/;
    if (key === 'Backspace' || key === 'Delete' || key === 'Tab' || key === 'ArrowLeft' || key === 'ArrowRight') {
      return;
    }
    if (!allowedPattern.test(key)) {
      event.preventDefault();
      return;
    }
    if (key === '.') {
      const currentValue = (event.target as HTMLInputElement).value;
      if (currentValue.includes('.')) {
        event.preventDefault();
      }
    }
  }

  /**
   * Keeps one decimal point and up to 2 fractional digits; preserves a trailing `.` so decimals can be entered
   * (parseFloat('12.') === 12 would otherwise strip the dot on each input event).
   * Preserves fractional zeros as typed (e.g. 12.0) so display does not collapse to "12".
   */
  private applyCurrencyAmountInputFormatting(
    event: Event,
    setControlValue: (v: string) => void,
    onPositiveAmountClear?: () => void
  ): void {
    const input = event.target as HTMLInputElement;
    let raw = input.value.replace(/[^0-9.]/g, '');
    const firstDot = raw.indexOf('.');
    if (firstDot !== -1) {
      const intPart = raw.slice(0, firstDot);
      let fracPart = raw.slice(firstDot + 1).replace(/\./g, '');
      const hadTrailingDot = raw.endsWith('.') && fracPart === '';
      fracPart = fracPart.slice(0, 2);
      if (hadTrailingDot && fracPart === '') {
        raw = intPart + '.';
      } else if (fracPart.length > 0) {
        raw = intPart + '.' + fracPart;
      } else {
        raw = intPart;
      }
    }
    if (raw.startsWith('.')) {
      raw = '0' + raw;
    }
    if (raw === '') {
      setControlValue('');
      input.value = '';
      return;
    }
    if (raw.endsWith('.')) {
      const intPart = raw.slice(0, -1);
      if (intPart === '') {
        input.value = '0.';
        setControlValue('0.');
        return;
      }
      const intNum = parseFloat(intPart.replace(/,/g, ''));
      if (isNaN(intNum)) {
        setControlValue('');
        input.value = '';
        return;
      }
      const display =
        new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(intNum) + '.';
      setControlValue(intPart + '.');
      input.value = display;
      if (intNum > 0 && onPositiveAmountClear) {
        onPositiveAmountClear();
      }
      return;
    }
    if (raw.includes('.') && !raw.endsWith('.')) {
      const dotIdx = raw.indexOf('.');
      const intPart = raw.slice(0, dotIdx);
      const fracPart = raw.slice(dotIdx + 1);
      const intNum = parseFloat(intPart.replace(/,/g, ''));
      if (isNaN(intNum)) {
        setControlValue('');
        input.value = '';
        return;
      }
      const intDisplay = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(intNum);
      const display = intDisplay + '.' + fracPart;
      setControlValue(raw);
      input.value = display;
      const num = parseFloat(raw.replace(/,/g, ''));
      if (!isNaN(num) && num > 0 && onPositiveAmountClear) {
        onPositiveAmountClear();
      }
      return;
    }
    const num = parseFloat(raw.replace(/,/g, ''));
    if (isNaN(num)) {
      setControlValue('');
      input.value = '';
      return;
    }
    setControlValue(String(num));
    input.value = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
    if (num > 0 && onPositiveAmountClear) {
      onPositiveAmountClear();
    }
  }

  formatFeeAmount(event: any, index: number): void {
    this.applyCurrencyAmountInputFormatting(event, (v) => {
      const feeGroup = this.slTaxFeeItems.at(index) as FormGroup;
      const feeAmountControl = feeGroup.get('feeAmount');
      if (feeAmountControl) {
        feeAmountControl.setValue(v, { emitEvent: false });
        feeAmountControl.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  formatPolicyFeeAmount(event: any): void {
    this.applyCurrencyAmountInputFormatting(event, (v) => {
      const control = this.bindIssuePolicyForm.get('policyFee');
      if (control) {
        control.setValue(v, { emitEvent: false });
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  formatSurplusLinesTaxAmount(event: any): void {
    this.applyCurrencyAmountInputFormatting(event, (v) => {
      const control = this.bindIssuePolicyForm.get('surplusLinesTax');
      if (control) {
        control.setValue(v, { emitEvent: false });
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  private normalizeAmountControlToTwoDecimals(control: AbstractControl | null | undefined): void {
    if (!control) {
      return;
    }
    const raw = control.value;
    if (raw === null || raw === undefined || String(raw).trim() === '') {
      return;
    }
    const numeric = this.parseAmount(raw);
    if (isNaN(numeric)) {
      return;
    }
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numeric);
    control.setValue(formatted, { emitEvent: false });
    control.updateValueAndValidity({ emitEvent: false });
  }

  onPolicyFeeBlur(): void {
    const control = this.bindIssuePolicyForm.get('policyFee');
    this.normalizeAmountControlToTwoDecimals(control);
    control?.markAsTouched();
  }

  onSurplusLinesTaxBlur(): void {
    const control = this.bindIssuePolicyForm.get('surplusLinesTax');
    this.normalizeAmountControlToTwoDecimals(control);
    control?.markAsTouched();
  }

  onFeeAmountBlur(index: number): void {
    const feeGroup = this.slTaxFeeItems.at(index) as FormGroup;
    const control = feeGroup.get('feeAmount');
    this.normalizeAmountControlToTwoDecimals(control);
    control?.markAsTouched();
  }

    allowStreetNumberCharacters(event: KeyboardEvent): void {
      const allowedPattern = /^[0-9a-zA-Z\-\/ .#]$/;  
      const key = event.key;

      // allow control keys like Backspace, Delete, Tab, Arrow keys
      if (
        key === 'Backspace' ||
        key === 'Delete' ||
        key === 'Tab' ||
        key === 'ArrowLeft' ||
        key === 'ArrowRight'
      ) {
        return;
      }

      if (!allowedPattern.test(key)) {
        event.preventDefault();
      }
    }

  allowOnlyAlphanumeric(event: KeyboardEvent): void {
    const key = event.key;
    
    // Check if the key is not an alphanumeric character (0-9, a-z, A-Z)
    if (!/^[a-zA-Z0-9 ]$/.test(key) && key !== "Backspace" && key !== "Delete" && key !== "ArrowLeft" && key !== "ArrowRight") {
        event.preventDefault(); // Prevent non-alphanumeric input
    }
  }

  hasReachedMaxFeeItems(): boolean {
    return false; // No maximum limit - users can add unlimited items
  }

  private isOtherFeeOptionId(optionId: number | string): boolean {
    const option = this.surplusLinesTaxFeeOptions().find((item: any) => Number(item?.gencodeId) === Number(optionId));
    if (!option) {
      return false;
    }
    const description = option.description?.toLowerCase();
    const code = option.code?.toLowerCase();
    return description === 'other' || code === 'other';
  }

  get slTaxFeeItems(): FormArray {
    return this.bindIssuePolicyForm.get('slTaxFeeDetails') as FormArray;
  }

  createTaxFeeGroup(detail: any = {}): FormGroup {
    const feeAmountValue = detail?.feeAmount !== undefined && detail?.feeAmount !== null ? detail?.feeAmount : '';
    const hasSlFeeId = detail?.slFeeId && detail?.slFeeId !== '';
    
    const group = this.fb.group({
      id: [detail?.id ?? 0],
      slFeeId: [detail?.slFeeId ?? ''],
      slFeeDescription: [detail?.slFeeDescription ?? ''],
      otherDescription: [detail?.otherDescription ?? ''],
      feeAmount: [
        feeAmountValue !== '' ? String(feeAmountValue) : '',
        hasSlFeeId 
          ? [Validators.required, noWhitespaceValidator, this.zeroAmountRequiredValidator(), Validators.pattern(this.currencyAmountPattern), this.feeAmountMaxValidator()]
          : [Validators.pattern(this.currencyAmountPattern), this.feeAmountMaxValidator()]
      ]
    });
    
    // Amount is required only when description is selected
    const slFeeIdControl = group.get('slFeeId');
    const feeAmountControl = group.get('feeAmount');
    
    if (slFeeIdControl && feeAmountControl) {
      slFeeIdControl.valueChanges.subscribe((value) => {
        if (value && value !== '') {
          feeAmountControl.setValidators([Validators.required, noWhitespaceValidator, this.zeroAmountRequiredValidator(), Validators.pattern(this.currencyAmountPattern), this.feeAmountMaxValidator()]);
        } else {
          feeAmountControl.setValidators([Validators.pattern(this.currencyAmountPattern), this.feeAmountMaxValidator()]);
        }
        feeAmountControl.updateValueAndValidity({ emitEvent: false });
      });
    }
    
    return group;
  }

  loadSurplusLinesTaxFees() {
    this.indicationQuoteService.GetSurplusLinesTaxFee().subscribe({
      next: (response: any) => {
        const options = Array.isArray(response) ? response.filter((item: any) => !!item) : [];
        
        // Find and store gencodeIds for Policy Fee and Surplus Lines Tax
        const policyFeeOption = options.find((item: any) => 
          item.description?.toLowerCase() === 'policy fee' || item.code?.toLowerCase() === 'policy fee'
        );
        const surplusLinesTaxOption = options.find((item: any) => 
          item.description?.toLowerCase() === 'surplus lines tax' || item.code?.toLowerCase() === 'surplus lines tax'
        );
        
        if (policyFeeOption) {
          this.policyFeeGencodeId.set(Number(policyFeeOption.gencodeId));
        }
        if (surplusLinesTaxOption) {
          this.surplusLinesTaxGencodeId.set(Number(surplusLinesTaxOption.gencodeId));
        }
        
        // Filter out Policy Fee and Surplus Lines Tax from dropdown options
        const filteredOptions = options.filter((item: any) => {
          const desc = item.description?.toLowerCase() || '';
          const code = item.code?.toLowerCase() || '';
          return desc !== 'policy fee' && code !== 'policy fee' && 
                 desc !== 'surplus lines tax' && code !== 'surplus lines tax';
        });
        
        this.surplusLinesTaxFeeOptions.set(filteredOptions);
        this.syncAllTaxFeeSelections();
        
        // Re-bind fee details now that gencodeIds are available
        if (this.cachedBislFeeDetails && this.cachedBislFeeDetails.length > 0) {
          this.setSurplusFeeDetails(this.cachedBislFeeDetails);
        }
      },
      error: () => {
        this.toastService.addNotification({message: "Failed to load surplus line tax/fee options. Please retry.", type: ToastType.Error});
      }
    });
  }

  syncAllTaxFeeSelections() {
    this.slTaxFeeItems.controls.forEach((_, index) => this.syncTaxFeeSelectionMetadata(index));
  }

  syncTaxFeeSelectionMetadata(index: number) {
    const group = this.slTaxFeeItems.at(index) as FormGroup;
    if (!group) {
      return;
    }
    const feeId = group.get('slFeeId')?.value;
    const option = this.surplusLinesTaxFeeOptions().find((item: any) => Number(item?.gencodeId) === Number(feeId));
    if (option) {
      group.get('slFeeDescription')?.setValue(option.description, { emitEvent: false });
    }
    this.handleOtherDescriptionValidator(group, this.isOtherFeeOption(feeId));
  }

  isOtherFeeOption(feeId: number | string): boolean {
    if (!feeId) {
      return false;
    }
    const option = this.surplusLinesTaxFeeOptions().find((item: any) => Number(item?.gencodeId) === Number(feeId));
    if (!option) {
      return false;
    }
    return option.description?.toLowerCase() === 'other' || option.code?.toLowerCase() === 'other';
  }

  handleOtherDescriptionValidator(group: FormGroup, shouldRequire: boolean) {
    const control = group.get('otherDescription');
    if (!control) {
      return;
    }
    if (shouldRequire) {
      control.setValidators([Validators.required, noWhitespaceValidator]);
    } else {
      control.clearValidators();
      control.setValue('', { emitEvent: false });
    }
    control.updateValueAndValidity();
  }

  shouldShowOtherDescription(index: number): boolean {
    const group = this.slTaxFeeItems.at(index) as FormGroup;
    return this.isOtherFeeOption(group?.get('slFeeId')?.value);
  }

  isFeeOptionDisabled(optionId: number, currentIndex: number): boolean {
    // Filter out Policy Fee and Surplus Lines Tax from dropdown options
    const option = this.surplusLinesTaxFeeOptions().find((item: any) => Number(item?.gencodeId) === Number(optionId));
    if (option) {
      const desc = option.description?.toLowerCase() || '';
      const code = option.code?.toLowerCase() || '';
      if (desc === 'policy fee' || code === 'policy fee' || 
          desc === 'surplus lines tax' || code === 'surplus lines tax') {
        return true; // Disable Policy Fee and Surplus Lines Tax in dropdown
      }
    }
    
    if (this.isOtherFeeOptionId(optionId)) {
      return false;
    }
    const selectedIds = this.slTaxFeeItems.controls
      .map((group, index) => index === currentIndex ? null : Number(group.get('slFeeId')?.value))
      .filter((value) => value !== null && value !== undefined && !this.isOtherFeeOptionId(Number(value)));
    return selectedIds.includes(Number(optionId));
  }

  addTaxFeeItem() {
    if (!this.canAddTaxFeeItem()) {
      return;
    }
    this.slTaxFeeItems.push(this.createTaxFeeGroup());
    this.syncAllTaxFeeSelections();
  }

  removeTaxFeeItem(index: number) {
    if (index < 0 || index >= this.slTaxFeeItems.length || this.isReadOnly() || this.isInceptionReferralLock()) {
      return;
    }
    this.slTaxFeeItems.removeAt(index);
    this.syncAllTaxFeeSelections();
  }

  canAddTaxFeeItem(): boolean {
    if (this.isReadOnly() || this.isInceptionReferralLock()) {
      return false;
    }
    // If no items exist, allow adding the first one
    if (this.slTaxFeeItems.length === 0) {
      return true;
    }
    // Check if the last item has both description and amount filled
    const lastIndex = this.slTaxFeeItems.length - 1;
    const lastGroup = this.slTaxFeeItems.at(lastIndex) as FormGroup;
    if (!lastGroup) {
      return false;
    }
    const slFeeId = lastGroup.get('slFeeId')?.value;
    const feeAmount = lastGroup.get('feeAmount')?.value;
    // Both description and amount must have values
    return !!(slFeeId && slFeeId !== '' && feeAmount && feeAmount !== '');
  }

  onTaxFeeDescriptionChange(index: number, event: Event) {
    const group = this.slTaxFeeItems.at(index) as FormGroup;
    const target = event.target as HTMLSelectElement;
    const selectedValue = target?.value ?? '';
    const selectedOption = this.surplusLinesTaxFeeOptions().find((item: any) => Number(item?.gencodeId) === Number(selectedValue));
    group.patchValue({
      slFeeId: selectedValue,
      slFeeDescription: selectedOption?.description || ''
    });
    this.handleOtherDescriptionValidator(group, this.isOtherFeeOption(selectedValue));
  }

  setSurplusFeeDetails(bislFeeDetails: any[]) {
    // Cache the fee details so we can re-bind once gencodeIds are loaded
    this.cachedBislFeeDetails = Array.isArray(bislFeeDetails) ? [...bislFeeDetails] : [];
    
    const feeArray = this.slTaxFeeItems;
    feeArray.clear();
    
    let policyFeeAmount = '';
    let surplusLinesTaxAmount = '';
    const otherFees: any[] = [];
    
    if (bislFeeDetails && bislFeeDetails.length > 0) {
      bislFeeDetails.forEach((detail: any) => {
        const feeAmount = detail?.feeAmount !== undefined && detail?.feeAmount !== null ? detail?.feeAmount : '';
        // Format the amount as currency when binding from API
        const formattedAmount = feeAmount !== '' && !isNaN(parseFloat(String(feeAmount))) 
          ? new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(parseFloat(String(feeAmount)))
          : '';
        
        const description = (detail?.slFeeDescription || '').toString().toLowerCase().trim();
        const slFeeId = Number(detail?.slFeeId);
        
        // Check if it's Policy Fee or Surplus Lines Tax
        // Match by description (normalized) OR by gencodeId if available
        const isPolicyFee = description === 'policy fee' || 
                           (this.policyFeeGencodeId() !== null && slFeeId === this.policyFeeGencodeId());
        
        const isSurplusTax = description === 'surplus lines tax' || 
                            (this.surplusLinesTaxGencodeId() !== null && slFeeId === this.surplusLinesTaxGencodeId());
       
        if (isPolicyFee) {
          policyFeeAmount = formattedAmount;
          
        } else if (isSurplusTax) {
          surplusLinesTaxAmount = formattedAmount;
          
        } else {
          // It's another fee type
          otherFees.push({
            id: detail?.id ?? 0,
            slFeeId: detail?.slFeeId ?? '',
            slFeeDescription: detail?.slFeeDescription ?? '',
            otherDescription: detail?.otherDescription ?? '',
            feeAmount: formattedAmount
          });
        }
      });
    }
    
    // Set Policy Fee and Surplus Lines Tax values
    const policyFeeControl = this.bindIssuePolicyForm.get('policyFee');
    if (policyFeeControl) {
      policyFeeControl.setValue('', { emitEvent: false });
    }
    if (policyFeeAmount) {
      const policyFeeControl = this.bindIssuePolicyForm.get('policyFee');
      if (policyFeeControl) {
        policyFeeControl.setValue(policyFeeAmount, { emitEvent: false });
      }
    }
    const surplusTaxControl = this.bindIssuePolicyForm.get('surplusLinesTax');
    if (surplusTaxControl) {
      surplusTaxControl.setValue('', { emitEvent: false });
    }
    if (surplusLinesTaxAmount) {
      const surplusTaxControl = this.bindIssuePolicyForm.get('surplusLinesTax');
      if (surplusTaxControl) {
        surplusTaxControl.setValue(surplusLinesTaxAmount, { emitEvent: false });
      }
    }
    
    // Add other fees to the form array
    if (otherFees.length > 0) {
      otherFees.forEach((fee) => {
        feeArray.push(this.createTaxFeeGroup(fee));
      });
    }
    // No default item - users must click "Add tax/fee Item" to add items
    
    this.syncAllTaxFeeSelections();
  }

  transformSurplusFeeData(data: any[], ownerId: number) {
    const bislFeeDetails: any[] = [];
    
    // Add Policy Fee if it has a value
    const policyFeeValue = this.bindIssuePolicyForm.get('policyFee')?.value;
    if (policyFeeValue && policyFeeValue !== '' && this.policyFeeGencodeId()) {
      bislFeeDetails.push({
        id: ownerId,
        slFeeId: this.policyFeeGencodeId(),
        slFeeDescription: 'Policy Fee',
        otherDescription: '',
        feeAmount: Number(this.parseAmount(policyFeeValue).toFixed(2))
      });
    }
    
    // Add Surplus Lines Tax if it has a value
    const surplusLinesTaxValue = this.bindIssuePolicyForm.get('surplusLinesTax')?.value;
    if (surplusLinesTaxValue && surplusLinesTaxValue !== '' && this.surplusLinesTaxGencodeId()) {
      bislFeeDetails.push({
        id: ownerId,
        slFeeId: this.surplusLinesTaxGencodeId(),
        slFeeDescription: 'Surplus Lines Tax',
        otherDescription: '',
        feeAmount: Number(this.parseAmount(surplusLinesTaxValue).toFixed(2))
      });
    }
    
    // Add other fee details from the form array
    if (data && data.length > 0) {
      const otherFees = data
        .filter((item) => item.slFeeId && item.feeAmount !== '' && item.feeAmount !== null && item.feeAmount !== undefined)
        .map((item) => ({
          id: ownerId,
          slFeeId: Number(item.slFeeId),
          slFeeDescription: item.slFeeDescription?.trim() || '',
          otherDescription: (this.isOtherFeeOption(item.slFeeId) || item.slFeeDescription?.toLowerCase() === 'other')
            ? (item.otherDescription?.trim() || '')
            : '',
          feeAmount: Number(this.parseAmount(item.feeAmount).toFixed(2))
        }));
      bislFeeDetails.push(...otherFees);
    }
    
    return bislFeeDetails;
  }
  
  getStateTooltipText(value: string){
    const getStateName = Array.from(this.states().values()).flat().find(s => String(s.stateId) === String(value));
    if(getStateName.length > 0){
      return getStateName[0].stateName;
    }
    return '';
  }

  get lossPayeeItems(): FormArray {
    return this.bindIssuePolicyForm.get('lossPayeeDetailsForm') as FormArray;
  }

  premisesItems(lossPayeeIndex: number): FormArray {
    return this.lossPayeeItems.at(lossPayeeIndex).get('premisesAddress') as FormArray;
  }

  removeLossPayee(index: number): void {
    this.lossPayeeItems.removeAt(index);

    this.states.update((map) => { map.delete(index); return map; });
    this.counties.update((map) => { map.delete(index); return map; });
    this.cities.update((map) => { map.delete(index); return map; });
    this.filteredStates.update((map) => { map.delete(index); return map; });
    this.filteredCounty.update((map) => { map.delete(index); return map; });
    this.filteredCity.update((map) => { map.delete(index); return map; });
    this.searchLPState.update((map) => { map.delete(index); return map; });
    this.searchLPCounty.update((map) => { map.delete(index); return map; });
    this.searchLPCity.update((map) => { map.delete(index); return map; });
  }

  addPremisesAddress(lossPayeeIndex: number): void {
    const premisesArray = this.premisesItems(lossPayeeIndex);
    const addressList = this.premisesAddressList();
    
    // If premises address list is empty, don't add anything
    // This can happen if user clicks "Add Loss Payee" before data is loaded
    if (!addressList || addressList.length === 0) {
      // If data isn't loaded yet, we'll populate it when data becomes available
      // For now, just return - the user will need to wait for data to load
      return;
    }
    
    addressList.forEach((address) => {
       const addressName = `${address.streetNum} ${address.streetName} ${address.city} ${address.state} ${address.county}`;
       const premisesAddressGroup = this.fb.group({
        premisesAddress: [addressName],
        premisesBuilding: [address.buildingNum],
        isPremisesChecked: [false],
        lossPayeeBuildingId: [0],
        payeeId: [0],
        quoteId: [''],
        buildingId: [address.buildingId],
        premisesAddressId: [address.addressid]
      });
      premisesArray.push(premisesAddressGroup);
    })

  }

  addLossPayee() {   
    this.setLossPayeeDetail();
  }
  
  // Method to check if premises address data is loaded
  isPremisesAddressDataLoaded(): boolean {
    return this.premisesAddressList() && this.premisesAddressList().length > 0;
  }
  
  // Method to populate premises addresses for existing loss payees when data becomes available
  populatePremisesAddressesForExistingLossPayees(): void {
    const addressList = this.premisesAddressList();
    if (!addressList || addressList.length === 0) {
      return;
    }
    
    // Check all existing loss payees and populate their premises addresses if empty
    for (let i = 0; i < this.lossPayeeItems.length; i++) {
      try {
        const premisesArray = this.premisesItems(i);
        // Only populate if the premises array is empty (data wasn't loaded when loss payee was added)
        if (premisesArray && premisesArray.length === 0) {
          addressList.forEach((address) => {
            const addressName = `${address.streetNum} ${address.streetName} ${address.city} ${address.state} ${address.county}`;
            const premisesAddressGroup = this.fb.group({
              premisesAddress: [addressName],
              premisesBuilding: [address.buildingNum],
              isPremisesChecked: [false],
              lossPayeeBuildingId: [0],
              payeeId: [0],
              quoteId: [''],
              buildingId: [address.buildingId],
              premisesAddressId: [address.addressid]
            });
            premisesArray.push(premisesAddressGroup);
          });
          // Mark the form array as touched to ensure change detection
          premisesArray.markAsTouched();
        }
      } catch (error) {
        // Silently handle any errors (e.g., if loss payee form structure is invalid)
      }
    }
  }
  
  onClearanceCheck(e: any) {
    this.isClearanceSelected = e.target.checked
  }

  onConfirmationPopup(message: string){
    const dialog = this.dialog.open(SaveConfirmationDialogComponent, {
      data: { message: message }
    });
    return dialog.afterClosed()
  }

  
   onInceptionPopup(message: string){
    const dialog = this.dialog.open(InceptionDateDialogComponent, {
      data: { message: message }
    });
    return dialog.afterClosed()
  }


  onReturnToDashboardMTA() {
    const dialog = this.dialog.open(ConfirmationDialogComponent, {
        data: { message: "Do you want to return to dashboard?" }
    });
    
    dialog.afterClosed().subscribe((result: boolean) => {
        if(result){
            const isQuoteMTA = sessionStorage.getItem('isQuoteMTA');
            if(isQuoteMTA){
                this.policyService.declineMTAByBroker(Number(this.authService.getUserDetailsFromStorage()?.policyId), true)
                .subscribe({
                    next: (response: any) => {
                        if(response){
                        this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
                        }
                    },
                    error: (error: any) => {
                        this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
                    }
                })
            }else{
                this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
            }
        }
    }); 
}

  onSaveIndicationHomeAction() {
    const irStatus = String(sessionStorage.getItem("inceptionStatus"));
    const isreferral = Number(sessionStorage.getItem("isreferral"))

    if(isreferral !== 1){
      if(this.authService.isPolicyType()){
          this.onReturnToDashboardMTA();
        }else{
          const msg = "Do you want to save before exiting?";
          this.onConfirmationPopup(msg)
              .subscribe((result: string) => {
                          if (result === 'cancel') {
              return;
          }
            if (result === 'exit') {// Don't Save
                this.indicationQuoteService.removeIndicationQuoteStorageData();
                this.router.navigate(['/quote']);
            }

            if (result === 'save') {       
                      this.onSubmitReferralinc(this.bindIssuePolicyForm.getRawValue(), true);
            }            
              });
            }
          }else{
                      this.router.navigate(['/quote']);

          }
  }


  handleSaveStep(quoteId: number) {
      this.onSaveStep("4d", "Quote", String(quoteId));
  }

  onSaveStep(step: string, type: string, id: string) {
    if (this.isReadOnly()) {
      return;
    }
    this.indicationQuoteService.saveStepForCurrentUser(step, type, id).subscribe({
        next: (response: any) => {
        },
        error: (error: any) => {
        }
    })
  }

  get producingAgentAddress() {
    return this.bindIssuePolicyForm.get('producingAgentAddress') as FormGroup;
  }

  get insuredMailingAddress() {
    return this.bindIssuePolicyForm.get('insuredMailingAddress') as FormGroup;
  }

  get slAddress (){
    return this.bindIssuePolicyForm.get('slAddress') as FormGroup;
  }

  loadStates():void{
    this._service.getStates().subscribe({
      next: (response: any) => {
        if(response){
          this.states.set(response);
          this.filteredStates.set(response);
        }
      },
      error: (error: any) => {
      }
    })
  }

  updateCountyAndCityList(index: number, countiesList: any[] = [], citiesList: any[] = []) {
    this.counties.update((map) => map.set(index, countiesList));
    this.cities.update((map) => map.set(index, citiesList));
    this.filteredCounty.update((map) => map.set(index, countiesList));
    this.filteredCity.update((map) => map.set(index, citiesList));
  } 

  fetchCountyAndCity(stateId: number, stateDropdownType: string, index: number = 0, countiesList: any[] = [], citiesList: any[] = []){
    this.isLoading.set(true);
    forkJoin([
      this._service.getCounties(stateId),
      this._service.getCities(stateId),
    ]).pipe(
      map(([countyList, cityList]) => { return {countyList, cityList}})
    ).subscribe({
      next: (response: any) => {
          if(response){
            const countiesSortedList = this._service.sortCountyRecords(response.countyList);
            const citiesSortedList = this._service.sortCityRecords(response.cityList);
            this.isLoading.set(false);

            if(stateDropdownType === 'LP'){
              if(countiesList?.length > 0 && citiesList?.length > 0){
                const filteredCounties = countiesSortedList.filter(county => countiesList.includes(county.countyId));
                const filteredCities = citiesSortedList.filter(city => citiesList.includes(city.cityId));
                this.updateCountyAndCityList(index, filteredCounties, filteredCities);
              }else{
                this.updateCountyAndCityList(index, countiesSortedList, citiesSortedList);
              }
            }else{
              if(countiesList?.length > 0 && citiesList?.length > 0){
                const filteredCounties = countiesSortedList.filter(county => countiesList.includes(county.countyId));
                const filteredCities = citiesSortedList.filter(city => citiesList.includes(city.cityId));
                this.updateCountyAndCityByStateControl(stateDropdownType, filteredCounties, filteredCities);
              }else{
                this.updateCountyAndCityByStateControl(stateDropdownType, countiesSortedList, citiesSortedList);
              }
            }
          }
      },
      error: (error: any) => {
        this.isLoading.set(false);
        this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    });
  }

  updateCountyAndCityByStateControl(stateDropDownType: string, countiesList: any[], citiesList: any[]){
    switch(stateDropDownType){
      case "PA": // Producing Agent form group
        this.countiesPA.set(countiesList);
        this.citiesPA.set(citiesList);
        this.filteredPACounty.set(countiesList);
        this.filteredPACity.set(citiesList);
        break;
      case "SI": // Surplus Information form group
        this.countiesSI.set(countiesList);
        this.citiesSI.set(citiesList);
        this.filteredSICounty.set(countiesList);
        this.filteredSICity.set(citiesList);
        break;
      case "PI": // Post Inspection bind form group
        this.countiesPI.set(countiesList);
        this.citiesPI.set(citiesList);
        this.filteredPICounty.set(countiesList);
        this.filteredPICity.set(citiesList);
        break;
    }
  }

  updateLPStateList(index: number, stateList: any[]) {
    const filteredStates = this.masterStateList().filter(state => stateList.includes(state.stateId));
    this.states.update((map) => map.set(index, filteredStates));
    this.filteredStates.update((map) => map.set(index, filteredStates));
  }

  fetchStateByLPZipCode(index: number, zipcode: string) {
    this._service.getZipCode(zipcode).subscribe({
      next: (response: any) => {
        if(response && response?.State?.length > 0){
          this.updateLPStateList(index, response?.State); // update State List
          this.fetchCountyAndCity(response?.State[0], "LP", index, response?.County, response?.City);
          this.lossPayeeItems.at(index).patchValue({cityId: response?.City[0], countyId: response?.County[0], stateId: response?.State[0]});
        }else{
          this.toastService.addNotification({message: "Invalid Zip Code, please enter a valid Zip Code", type: ToastType.Error}); // error notification
          this.lossPayeeItems.at(index).patchValue({cityId: '', countyId: '', stateId: ''});
          this.states.update((map) => map.set(index, [])); // reset the list for invalid record
          this.counties.update((map) => map.set(index, [])); // reset the list for invalid record
          this.cities.update((map) => map.set(index, [])); // reset the list for invalid record
          this.filteredStates.update((map) => map.set(index, [])); // reset the list for invalid record
          this.filteredCounty.update((map) => map.set(index, [])); // reset the list for invalid record
          this.filteredCity.update((map) => map.set(index, [])); // reset the list for invalid record
        }
      },
      error: (error: any) => {
        this.lossPayeeItems.at(index).patchValue({cityId: '', countyId: '', stateId: ''});
        this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    });
  }

  updateStateCountyCityList(data: any, dropdownType: string) {
    const {City, County, State} = data;
    const filteredStates = this.masterStateList().filter(state => State.includes(state.stateId));
    switch(dropdownType){
      case "PA":
        this.statesPA.set(filteredStates);
        this.filteredPAState.set(filteredStates);
        this.bindIssuePolicyForm.get('producingAgentAddress.stateId')?.patchValue(State[0]);
        this.bindIssuePolicyForm.get('producingAgentAddress.cityId')?.patchValue(City[0]);
        this.bindIssuePolicyForm.get('producingAgentAddress.countyId')?.patchValue(County[0]);
        break;
      case "SI":
        this.statesSI.set(filteredStates);
        this.filteredSIState.set(filteredStates);
        this.bindIssuePolicyForm.get('slAddress.stateId')?.patchValue(State[0]);
        this.bindIssuePolicyForm.get('slAddress.cityId')?.patchValue(City[0]);
        this.bindIssuePolicyForm.get('slAddress.countyId')?.patchValue(County[0]);
        break;
      case "PI":
        this.statesPI.set(filteredStates);
        this.filteredPIState.set(filteredStates);
        this.bindIssuePolicyForm.get('insuredMailingAddress.stateId')?.patchValue(State[0]);
        this.bindIssuePolicyForm.get('insuredMailingAddress.cityId')?.patchValue(City[0]);
        this.bindIssuePolicyForm.get('insuredMailingAddress.countyId')?.patchValue(County[0]);
        break;
    }
  }

  resetDropdownFilterData(dropdownType: string){
    switch(dropdownType){
      case "PA":
        this.statesPA.set([]);
        this.countiesPA.set([]);
        this.citiesPA.set([]);
        this.filteredPAState.set([]);
        this.filteredPACounty.set([]);
        this.filteredPACity.set([]);
        this.bindIssuePolicyForm.get('producingAgentAddress.stateId')?.patchValue('');
        this.bindIssuePolicyForm.get('producingAgentAddress.cityId')?.patchValue('');
        this.bindIssuePolicyForm.get('producingAgentAddress.countyId')?.patchValue('');
        break;
      case "SI":
        this.statesSI.set([]);
        this.countiesSI.set([]);
        this.citiesSI.set([]);
        this.filteredSIState.set([]);
        this.filteredSICounty.set([]);
        this.filteredSICity.set([]);
        this.bindIssuePolicyForm.get('slAddress.stateId')?.patchValue('');
        this.bindIssuePolicyForm.get('slAddress.cityId')?.patchValue('');
        this.bindIssuePolicyForm.get('slAddress.countyId')?.patchValue('');
        break;
      case "PI":
        this.statesPI.set([]);
        this.countiesPI.set([]);
        this.citiesPI.set([]);
        this.filteredPIState.set([]);
        this.filteredPICounty.set([]);
        this.filteredPICity.set([]);
        this.bindIssuePolicyForm.get('insuredMailingAddress.stateId')?.patchValue('');
        this.bindIssuePolicyForm.get('insuredMailingAddress.cityId')?.patchValue('');
        this.bindIssuePolicyForm.get('insuredMailingAddress.countyId')?.patchValue('');
        break;
    }
  }

  fetchStateByZipCode(zipcode: string, dropdownType: string) {
    this._service.getZipCode(zipcode).subscribe({
      next: (response: any) => {
        if(response && response?.State?.length > 0){
          this.updateStateCountyCityList(response, dropdownType); // update State List
          this.fetchCountyAndCity(response?.State[0], dropdownType, response?.County, response?.City);
        }else{
          this.toastService.addNotification({message: "Invalid Zip Code, please enter a valid Zip Code", type: ToastType.Error}); // error notification
          this.resetDropdownFilterData(dropdownType);
        }
      },
      error: (error: any) => {
        this.resetDropdownFilterData(dropdownType);
        this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    });
  }

  onLPZipCodeBlur(index: number) {
    const zipcode = this.lossPayeeItems.at(index).get('zipCode')?.value;
    if (zipcode?.length >= 1) {
      this.fetchStateByLPZipCode(index, zipcode);
    }
  }

  onPAZipCodeBlur() {
    const zipcode = this.producingAgentAddress.get('zipCode')?.value;
    if (zipcode?.length >= 1) {
      this.fetchStateByZipCode(zipcode, "PA");
    }
  }

  onSIZipCodeBlur() {
    const zipcode = this.slAddress.get('zipCode')?.value;
    if (zipcode?.length >= 1) {
      this.fetchStateByZipCode(zipcode, "SI");
    }
  }

  onPIZipCodeBlur() {
    const zipcode = this.insuredMailingAddress.get('zipCode')?.value;
    if (zipcode?.length >= 1) {
      this.fetchStateByZipCode(zipcode, "PI");
    }
  }

  // Method to fetch city name by ID
  getCityName(cityId: string | null): string {
    if (!cityId) return '';
    const city = Array.from(this.cities().values()).flat().find(c => c.cityId === cityId);
    return city ? city.cityName : '';
  }

  getPACityName(cityId: string | null): string {
    if (!cityId) return '';
    const city = this.citiesPA().find(c => c.cityId === cityId);
    return city ? city.cityName : '';
  }

  getSICityName(cityId: string | null): string {
    if (!cityId) return '';
    const city = this.citiesSI().find(c => c.cityId === cityId);
    return city ? city.cityName : '';
  }

  getPICityName(cityId: string | null): string {
    if (!cityId) return '';
    const city = this.citiesPI().find(c => c.cityId === cityId);
    return city ? city.cityName : '';
  }

  // Method to fetch state name by ID
  getStateName(stateId: string | null): string {
    if (!stateId) return '';
    const state =Array.from(this.states().values()).flat().find(s => s.stateId === stateId);
    return state ? state.stateName : '';
  }

  // Method to fetch county name by ID
  getCountyName(countyId: string | null): string {
    if (!countyId) return '';
    const county = Array.from(this.counties().values()).flat().find(c => c.countyId === countyId);
    return county ? county.countyName : '';
  }

  getPAStateName(stateId: string | null): string {
    if (!stateId) return '';
    const state =Array.from(this.statesPA().values()).flat().find(s => s.stateId === stateId);
    return state ? state.stateName : '';
  }

  getPACountyName(countyId: string | null): string {
    if (!countyId) return '';
    const county = this.countiesPA().find(c => c.countyId === countyId);
    return county ? county.countyName : '';
  }

  getSIStateName(stateId: string | null): string {
    if (!stateId) return '';
    const state =Array.from(this.statesSI().values()).flat().find(s => s.stateId === stateId);
    return state ? state.stateName : '';
  }

  getSICountyName(countyId: string | null): string {
    if (!countyId) return '';
    const county = this.countiesSI().find(c => c.countyId === countyId);
    return county ? county.countyName : '';
  }

  getPIStateName(stateId: string | null): string {
    if (!stateId) return '';
    const state =Array.from(this.statesPI().values()).flat().find(s => s.stateId === stateId);
    return state ? state.stateName : '';
  }

  getPICountyName(countyId: string | null): string {
    if (!countyId) return '';
    const county = this.countiesPI().find(c => c.countyId === countyId);
    return county ? county.countyName : '';
  }

  // updateFilteredStates(value: string) {
  //   const filtered = this.states().filter((state) =>
  //     state.stateName.toLowerCase().includes(value.toLowerCase())
  //   );
  //   this.filteredStates.set(filtered);
  // }

  updateFilteredCounty(value: string, index: number) {
    const counties = this.counties().get(index) || [];
    const filtered = counties.filter((county) =>
      county.countyName.toLowerCase().includes(value.toLowerCase())
    );
    this.filteredCounty.update((map) => map.set(index, filtered));
  }

  updateFilteredCity(value: string, index: number) {
    const cities = this.cities().get(index) || [];
    const filtered = cities.filter((city) =>
      city.cityName.toLowerCase().includes(value.toLowerCase())
    );
    this.filteredCity.update((map) => map.set(index, filtered));
  }

  onLPStateSearch(index: number, event: Event) {
    const value = (event.target as HTMLInputElement).value; 

    this._service.applyDelayedFilter(`LPState-${index}`, 1200, () => {
      this.updateLPStateSearchTerm(index, value);
    });
  }

  updateLPStateSearchTerm(index: number, value: string) {
    //const value = event.target ? (event.target as HTMLInputElement).value : '';
    this.searchLPState.update((map) => map.set(index, value));
    const states = this.states().get(index) || [];
    const filtered = states.filter((state) =>
      state.stateName.toLowerCase().includes(value.toLowerCase())
    );
    this.filteredStates.update((map) => map.set(index, filtered));
  }

  onPAStateSearch(event: Event) {
    const value = (event.target as HTMLInputElement).value; 

    this._service.applyDelayedFilter("PAState", 1200, () => {
      this.updatePAStateSearchTerm(value);
    });
  }
  
  updatePAStateSearchTerm(value: string) {
    //const value = event.target ? (event.target as HTMLInputElement).value : '';
    this.searchPAState.set(value);
    const filtered = this.statesPA().filter((state) =>
      state.stateName.toLowerCase().includes(value.toLowerCase())
    );
    this.filteredPAState.set(filtered);
  }

  onSIStateSearch(event: Event) {
    const value = (event.target as HTMLInputElement).value; 

    this._service.applyDelayedFilter("SIState", 1200, () => {
      this.updateSIStateSearchTerm(value);
    });
  }

  updateSIStateSearchTerm(value: string) {
    //const value = event.target ? (event.target as HTMLInputElement).value : '';
    this.searchSIState.set(value);
    const filtered = this.statesSI().filter((state) =>
      state.stateName.toLowerCase().includes(value.toLowerCase())
    );
    this.filteredSIState.set(filtered);
  }

  onPIStateSearch(event: Event) {
    const value = (event.target as HTMLInputElement).value; 

    this._service.applyDelayedFilter("PIState", 1200, () => {
      this.updatePIStateSearchTerm(value);
    });
  }
  
  updatePIStateSearchTerm(value: string) {
    //const value = event.target ? (event.target as HTMLInputElement).value : '';
    this.searchPIState.set(value);
    const filtered = this.statesPI().filter((state) =>
      state.stateName.toLowerCase().includes(value.toLowerCase())
    );
    this.filteredPIState.set(filtered);
  }

  onLPCountySearch(index: number, event: Event) {
    const value = (event.target as HTMLInputElement).value; 

    this._service.applyDelayedFilter(`LPCounty-${index}`, 1200, () => {
      this.updateLPCountySearchTerm(index, value);
    });
  }

  updateLPCountySearchTerm(index: number, value: string) {
    //const value = event.target ? (event.target as HTMLInputElement).value : '';
    this.searchLPCounty.update((map) => map.set(index, value));
    this.updateFilteredCounty(value, index);
  }

  onPACountySearch(event: Event) {
    const value = (event.target as HTMLInputElement).value; 

    this._service.applyDelayedFilter("PACounty", 1200, () => {
      this.updatePACountySearchTerm(value);
    });
  }

  updatePACountySearchTerm(value: string) {
    //const value = event.target ? (event.target as HTMLInputElement).value : '';
    this.searchPACounty.set(value);
    const filtered = this.countiesPA().filter((county) =>
      county.countyName.toLowerCase().includes(value.toLowerCase())
    );
    this.filteredPACounty.set(filtered);
  }

  onSICountySearch(event: Event) {
    const value = (event.target as HTMLInputElement).value; 

    this._service.applyDelayedFilter("SICounty", 1200, () => {
      this.updateSICountySearchTerm(value);
    });
  }

  updateSICountySearchTerm(value: string) {
    //const value = event.target ? (event.target as HTMLInputElement).value : '';
    this.searchSICounty.set(value);
    const filtered = this.countiesSI().filter((county) =>
      county.countyName.toLowerCase().includes(value.toLowerCase())
    );
    this.filteredSICounty.set(filtered);
  }

  onPICountySearch(event: Event) {
    const value = (event.target as HTMLInputElement).value; 

    this._service.applyDelayedFilter("PICounty", 1200, () => {
      this.updatePICountySearchTerm(value);
    });
  }

  updatePICountySearchTerm(value: string) {
    //const value = event.target ? (event.target as HTMLInputElement).value : '';
    this.searchPICounty.set(value);
    const filtered = this.countiesPI().filter((county) =>
      county.countyName.toLowerCase().includes(value.toLowerCase())
    );
    this.filteredPICounty.set(filtered);
  }

  onLPCitySearch(index: number, event: Event) {
    const value = (event.target as HTMLInputElement).value; 

    this._service.applyDelayedFilter(`LPCity-${index}`, 1200, () => {
      this.updateLPCitySearchTerm(index, value);
    });
  }

  updateLPCitySearchTerm(index: number, value: string) {
    //const value = event.target ? (event.target as HTMLInputElement).value : '';
    this.searchLPCity.update((map) => map.set(index, value));
    this.updateFilteredCity(value, index);
  }

  onPACitySearch(event: Event) {
    const value = (event.target as HTMLInputElement).value; 

    this._service.applyDelayedFilter("PACity", 1200, () => {
      this.updatePACitySearchTerm(value);
    });
  }

  updatePACitySearchTerm(value: string) {
    //const value = event.target ? (event.target as HTMLInputElement).value : '';
    this.searchPACity.set(value);
    const filtered = this.citiesPA().filter((city) =>
      city.cityName.toLowerCase().includes(value.toLowerCase())
    );
    this.filteredPACity.set(filtered);
  }

  onSICitySearch(event: Event) {
    const value = (event.target as HTMLInputElement).value; 

    this._service.applyDelayedFilter("SICity", 1200, () => {
      this.updateSICitySearchTerm(value);
    });
  }

  updateSICitySearchTerm(value: string) {
    //const value = event.target ? (event.target as HTMLInputElement).value : '';
    this.searchSICity.set(value);
    const filtered = this.citiesSI().filter((city) =>
      city.cityName.toLowerCase().includes(value.toLowerCase())
    );
    this.filteredSICity.set(filtered);
  }

  onPICitySearch(event: Event) {
    const value = (event.target as HTMLInputElement).value; 

    this._service.applyDelayedFilter("PICity", 1200, () => {
      this.updatePICitySearchTerm(value);
    });
  }

  updatePICitySearchTerm(value: string) {
    //const value = event.target ? (event.target as HTMLInputElement).value : '';
    this.searchPICity.set(value);
    const filtered = this.citiesPI().filter((city) =>
      city.cityName.toLowerCase().includes(value.toLowerCase())
    );
    this.filteredPICity.set(filtered);
  }

  // reset filtered states options
  onLPStateDropdownOpened(index: number, isOpen: boolean) {
    if (!isOpen) {
      this.searchLPState.update((map) => map.set(index, ''));
      const states = this.states().get(index) || [];
      this.filteredStates.update((map) => map.set(index, states));
    }
  }

  onStateDropdownOpened(isOpen: boolean, dropdownType: string) {
    if (!isOpen) {
      switch(dropdownType){
        case "PA":
          this.searchPAState.set('');
          this.filteredPAState.set([...this.statesPA()]);
          break;
        case "SI":
          this.searchSIState.set('');
          this.filteredSIState.set([...this.statesSI()]);
          break;
        case "PI":
          this.searchPIState.set('');
          this.filteredPIState.set([...this.statesPI()]);
          break;
      }
    }
  }

  // reset filtered county options
  onLPCountyDropdownOpened(index: number, isOpen: boolean) {
    if (!isOpen) {
      this.searchLPCounty.update((map) => map.set(index, ''));
      const counties = this.counties().get(index) || [];
      this.filteredCounty.update((map) => map.set(index, counties));
    }
  }

  onCountyDropdownOpened(isOpen: boolean, dropDownType: string) {
    if (!isOpen) {
      this.searchPACounty.set('');
      this.searchSICounty.set('');
      this.searchPICounty.set('');
      if(dropDownType === "PA"){
        const filtered = [...this.countiesPA()];
        this.filteredPACounty.set(filtered);
      }else if(dropDownType === "SI"){
        const filtered = [...this.countiesSI()];
        this.filteredSICounty.set(filtered);
      }else if(dropDownType === "PI"){
        const filtered = [...this.countiesPI()];
        this.filteredPICounty.set(filtered);
      }
    }
  }

  // reset filtered county options
  onLPCityDropdownOpened(index: number, isOpen: boolean) {
    if (!isOpen) {
      this.searchLPCity.update((map) => map.set(index, ''));
      const cities = this.cities().get(index) || [];
      this.filteredCity.update((map) => map.set(index, cities));
    }
  }

  onCityDropdownOpened(isOpen: boolean, dropDownType: string) {
    if (!isOpen) {
      this.searchPACity.set('');
      this.searchSICity.set('');
      this.searchPICity.set('');
      if(dropDownType === "PA"){
        const filtered = [...this.citiesPA()];
        this.filteredPACity.set(filtered);
      }else if(dropDownType === "SI"){
        const filtered = [...this.citiesSI()];
        this.filteredSICity.set(filtered);
      }else if(dropDownType === "PI"){
        const filtered = [...this.citiesPI()];
        this.filteredPICity.set(filtered);
      }
    }
  }

  savePolicyDetails(quoteId: number, userId: number, versionNo: number, isSaveAndExist: boolean = false) {
    this.indicationQuoteService.savePolicyDetails(quoteId, userId, versionNo).subscribe({
      next: (response: any) => {
        if(response){
          this.toastService.addNotification({message: "Data saved successfully", type: ToastType.Success}); // success notification
          if(isSaveAndExist){
            this.indicationQuoteService.removeIndicationQuoteStorageData();
            this.router.navigate(['/quote']);
          }else{
            this.isLoading.set(false)
            const { result } = response;
            this.indicationQuoteService.setPolicyId(Number(result)); // set policy Id
            this.enableViewAndDownloadDocumentSignal.set(true); // for showing view & download link
            this.checkDownloadEmailCompleteStatus();
            sessionStorage.setItem("policyDate", String(this.bindIssuePolicyForm.get('policyStartDate')?.value));
            sessionStorage.setItem('isPolicyType', 'true'); // for showing Step 5 accordion dynamically
            sessionStorage.setItem('isReview', 'true');
            this.onBindAndIssuePolicyEmitter.emit();
            this.bindIssuePolicyForm.disable();
            this.requestQuoteService.onUpdateTargetPremium(true); 
          }
          this.handleSaveStep(quoteId);
          this.toastService.addNotification({message: "Data saved successfully", type: ToastType.Success}); // error notification
        }
      },
      error: (error: any) => {
        this.isLoading.set(false)
        this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    })
  }

  handleBindAndIssuePolicySave(payload: any, isSaveAndExist: boolean = false) {
    const userId = Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId);
    const quoteId1 = Number(sessionStorage.getItem("quoteId"));
    const quoteVersionBound = Number(sessionStorage.getItem("quoteVersionBound"));
    const quoteId = Number(this.selectedQuote()?.quoteId) ? Number(this.selectedQuote()?.quoteId) : quoteId1;
    const versionNo = Number(this.selectedQuote()?.versionNo) ? Number(this.selectedQuote()?.versionNo) : quoteVersionBound;
 

    forkJoin([
      this.indicationQuoteService.saveBindIssuePolicyDetails(payload, quoteId, versionNo, userId),
      this.indicationQuoteService.savePolicyDetails(quoteId, userId, versionNo),
    ]).pipe(
      map(([bindAndIssuePolicy, policyDetails]) => { return {bindAndIssuePolicy, policyDetails}})
    ).subscribe({
      next: (response: any) => {
        if(response){
          this.toastService.addNotification({message: "Data saved successfully", type: ToastType.Success}); // Success notification
          if(isSaveAndExist){
            this.indicationQuoteService.removeIndicationQuoteStorageData();
            this.router.navigate(['/quote']);
          }else{
            const { result } = response.policyDetails; // Get Policy Id from savePolicy API
            this.indicationQuoteService.setPolicyId(Number(result)); // set the Policy ID
            //this.policyDocumentService.setDownloadPolicyDocument(true); // start the document binding process
            this.enableViewAndDownloadDocumentSignal.set(true); // for showing view & download link
            this.checkDownloadEmailCompleteStatus();
            sessionStorage.setItem("policyDate", String(this.bindIssuePolicyForm.get('policyStartDate')?.value));
            sessionStorage.setItem('isPolicyType', 'true'); // for showing Step 5 accordion dynamically
            sessionStorage.setItem('isReview', 'true');
            this.onBindAndIssuePolicyEmitter.emit();
            this.bindIssuePolicyForm.disable();
          }
          this.handleSaveStep(quoteId);
        }
      },
      error: (error: any) => {
        this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    })
  }

    handleBindAndIssuePolicySaveByQuoteIdForInceptionDate(payload: any, isSaveAndExist: boolean = false) {
    this.isLoading.set(true)
   const userId = Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId);
    const quoteId1 = Number(sessionStorage.getItem("quoteId"));
    const quoteVersionBound = Number(sessionStorage.getItem("quoteVersionBound"));
    const quoteId = Number(this.selectedQuote()?.quoteId) ? Number(this.selectedQuote()?.quoteId) : quoteId1;
    const versionNo = Number(this.selectedQuote()?.versionNo) ? Number(this.selectedQuote()?.versionNo) : quoteVersionBound;

    this.indicationQuoteService.saveBindIssuePolicyDetails(payload, quoteId, versionNo, userId).subscribe({
      next: (response: any) => {
        if(response){
          this.isLoading.set(false)
          if(isSaveAndExist){
            this.handleSaveStep(Number(quoteId));
            this.router.navigate(['/quote']);
            return;
          }
          this.onSetStatusId(IndicationInceptionStatus.STATUS_IDR);
            this.submitReferralQuote()
          
        }
      },
      error: (error: any) => {
        this.isLoading.set(false)
        this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    })
  }

  handleBindAndIssuePolicySaveByQuoteId(payload: any, isSaveAndExist: boolean = false) {
    this.isLoading.set(true)
    const userId = Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId);
    const quoteId1 = Number(sessionStorage.getItem("quoteId"));
    const quoteVersionBound = Number(sessionStorage.getItem("quoteVersionBound"));
    const quoteId = Number(this.selectedQuote()?.quoteId) ? Number(this.selectedQuote()?.quoteId) : quoteId1;
    const versionNo = Number(this.selectedQuote()?.versionNo) ? Number(this.selectedQuote()?.versionNo) : quoteVersionBound;

    this.indicationQuoteService.saveBindIssuePolicyDetails(payload, quoteId, versionNo, userId).subscribe({
      next: (response: any) => {
        if(response){
          this.savePolicyDetails(quoteId, userId, versionNo, isSaveAndExist);
        }
      },
      error: (error: any) => {
        this.isLoading.set(false)
        this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    })
  }

  handleBindAndIssuePolicySaveByPolicyId(payload: any) {
    this.indicationQuoteService.saveBindIssuePolicyDetailsByPolicyId(payload, Number(this.authService.getUserDetailsFromStorage()?.policyId), 
      Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId)).subscribe({
        next: (response: any) => {
          if(response){
            this.toastService.addNotification({message: "Data saved successfully", type: ToastType.Success}); // Success notification
              sessionStorage.setItem("policyDate", String(this.bindIssuePolicyForm.get('policyStartDate')?.value));
              this.onBindAndIssuePolicyEmitter.emit();
          }
        },
        error: (error: any) => {
          this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
        }
    });
  }

  transformLossPayeeData(data: any[]) {
    
    const quoteId1 = Number(sessionStorage.getItem("quoteId"));
    const quotePolicyId = this.authService.getUserDetailsFromStorage()?.isPolicyType
      ? this.authService.getUserDetailsFromStorage()?.policyId
      : this.selectedQuote()?.quoteId || quoteId1;
  
    const transformedData = {
      lossPayees: data.map((item) => ({
        lossPayeeId: item.lossPayeeId || 0,
        lossPayeeName: item.lossPayeeName?.trim() || "",
        quoteId: quotePolicyId,
        payeeApplicableClause: item.payeeApplicableClause || 0,
        payeeAddress: {
          quoteAddressId: item.quoteAddressId || 0,
          addressTypeId: item.addressTypeId || 0,
          quoteId: quotePolicyId,
          unitNum: item.unitNum?.trim() || "",
          streetNum: String(item.streetNum)?.trim(),                      
          streetName: item.streetName?.trim() || "",
          countyId: item.countyId || 0,
          stateId: item.stateId || 0,
          cityId: item.cityId || 0,
          zipCode: item.zipCode?.trim() || "",
          createdUserId: this.authService?.getUserDetailsFromStorage()?.userDetails?.userId,
        },
        premisesAddress: item.premisesAddress
          .filter((address: any) => address.isPremisesChecked === true)
          .map((premises: any) => ({
            lossPayeeBuildingId: premises.lossPayeeBuildingId || 0,
            payeeId: premises.payeeId || 0,
            quoteId: quotePolicyId,
            buildingId: premises.buildingId || 0,
            PremisesAddressId: premises.premisesAddressId || 0,
          })),
      })),
    };
  
    return transformedData;
  }

    onSetStatusId(statusName: string) {
    const getInprogressStatusList = this.InceptionStatusList().filter((status) => status.description === statusName);
    
    if(getInprogressStatusList.length > 0){
      this.brokerQuoteService.setInceptionStatusId(getInprogressStatusList[0]?.code); // Indication inception Referral Inprogress Code
    }
  }

  submitReferralQuote(){
    const quoteId = sessionStorage.getItem("quoteId");
    const branchId = sessionStorage.getItem("branchId");   
    const Status = this.brokerQuoteService.getInceptionStatusCode();
    const quoteVersionBound = Number(sessionStorage.getItem("quoteVersionBound"));
    const selectedQuoteVersionFromStorage = this.indicationQuoteService.getSelectedQuoteVersion();
    this.selectedQuote.set(selectedQuoteVersionFromStorage);
    const versionNo = selectedQuoteVersionFromStorage?.versionNo ? selectedQuoteVersionFromStorage?.versionNo : quoteVersionBound;

    this.indicationQuoteService.SubmitReferralInceptionDate(Number(quoteId),Number(branchId),String(Status),Number(versionNo)).subscribe({
      next: (response) => {
          if(response){
              this.handleSaveStep(Number(quoteId));
              this.toastService.addNotification({message: "Your referral has been successfully submitted", type: ToastType.Success}); 
              this.router.navigate(['/quote'])
              this.indicationQuoteService.removeIndicationQuoteStorageData();
          }
      },
      error: (error) => {
      }
      })
  }

  bindAndIssuePolicyDetailsPayloadHandler(payload: any, isSaveAndExit: boolean) {
        const quoteId1 = Number(sessionStorage.getItem("quoteId"));

    const quotePolicyId = this.authService.getUserDetailsFromStorage()?.isPolicyType ? 
    this.authService.getUserDetailsFromStorage()?.policyId : this.selectedQuote()?.quoteId || quoteId1;
    const {policyStartDate, PolicyEndDate, ProductingAgentCompanyName ,productingAgentName, productingAgentEmail, producingAgentPhone,InsuredPhoneNumber, producerName, 
      surplusNumber, insuredContactName, insuredEmailAddress, lossPayeeDetailsForm, producingAgentAddress, 
      insuredMailingAddress, slAddress, triaAcceptance, eqbdAcceptance, slTaxFeeDetails
    } = payload;  

    const rawPhoneNumber = producingAgentPhone.replace(/\D/g, '');
    const rawPhoneNumberinsured = InsuredPhoneNumber.replace(/\D/g, '');
    const producingAddressPayload = {
      ...producingAgentAddress,
      quoteId: quotePolicyId,
      unitNum: producingAgentAddress.unitNum?.trim(),
      streetNum: typeof producingAgentAddress.streetNum === 'string' ? producingAgentAddress.streetNum.trim() : producingAgentAddress.streetNum?.toString().trim() || "",
      streetName: producingAgentAddress.streetName?.trim(),
      cityId: producingAgentAddress.cityId ? producingAgentAddress.cityId : 0,
      stateId: producingAgentAddress.stateId ? producingAgentAddress.stateId : 0,
      countyId: producingAgentAddress.countyId ? producingAgentAddress.countyId : 0,
      zipCode: producingAgentAddress.zipCode,
    };    
    
    
    const slAddressPayload = {
      ...slAddress,
      quoteId: quotePolicyId,
      unitNum: slAddress.unitNum?.trim(),
      streetNum: typeof slAddress.streetNum === 'string' ? slAddress.streetNum.trim() : slAddress.streetNum?.toString().trim() || "",
      streetName: slAddress.streetName?.trim(),
      cityId: slAddress.cityId ? slAddress.cityId : 0 ,
      stateId: slAddress.stateId ? slAddress.stateId : 0,
      countyId: slAddress.countyId ? slAddress.countyId : 0,
      zipCode: slAddress.zipCode,
    };
    const insuranceMailingAddressPayload = {
      ...insuredMailingAddress,
      quoteId: quotePolicyId,
      unitNum: insuredMailingAddress.unitNum?.trim(),
      streetNum: typeof insuredMailingAddress.streetNum === 'string' ? insuredMailingAddress.streetNum.trim() : insuredMailingAddress.streetNum?.toString().trim() || "",
      streetName: insuredMailingAddress.streetName?.trim(),
      cityId: insuredMailingAddress.cityId ? insuredMailingAddress.cityId : 0,
      stateId: insuredMailingAddress.stateId ? insuredMailingAddress.stateId : 0,
      countyId: insuredMailingAddress.countyId ? insuredMailingAddress.countyId : 0,
      zipCode: insuredMailingAddress.zipCode,
    };
    const bindIssuePolicyPayload = {
      policyStartDate: policyStartDate,
      PolicyEndDate: PolicyEndDate,
      triaAcceptance: triaAcceptance,
      IsEQBDSelected: Number(eqbdAcceptance) === 1 ? 1 : 0,
      IsEQBDConfirmed:
        eqbdAcceptance === null || eqbdAcceptance === undefined || eqbdAcceptance === ''
          ? null
          : (Number(eqbdAcceptance) === 1 ? 1 : 0),
      lossPayees: this.transformLossPayeeData(lossPayeeDetailsForm)?.lossPayees,
      productingAgentName: productingAgentName?.trim(),
      productingAgentEmail: productingAgentEmail?.trim(),
      ProductingAgentCompanyName: ProductingAgentCompanyName?.trim(),
      producingAgentPhone: rawPhoneNumber,
      InsuredPhoneNumber:rawPhoneNumberinsured,
      producingAgentAddress: producingAddressPayload,
      producerName: producerName?.trim(),
      surplusNumber: surplusNumber === 'Others' 
        ? this.bindIssuePolicyForm.get('surplusOther')?.value?.trim() 
        : surplusNumber?.trim(),
      slAddress: slAddressPayload,
      insuredContactName: insuredContactName?.trim(),
      insuredEmailAddress: insuredEmailAddress?.trim(),
      insuredMailingAddress: insuranceMailingAddressPayload,
      bislFeeDetails: this.transformSurplusFeeData(slTaxFeeDetails, quotePolicyId)
    };

    const proceed = () => {
      if(this.authService.isPolicyType()){
        this.handleBindAndIssuePolicySaveByPolicyId(bindIssuePolicyPayload);
      }else{
        this.handleBindAndIssuePolicySaveByQuoteId(bindIssuePolicyPayload, isSaveAndExit);
      }
    };

    if (this.isNewJerseyState()) {
      this.saveNJTNData(isSaveAndExit).subscribe((success: boolean) => {
        if (!success) {
          return; // Do not proceed if NJTN save failed
        }
        proceed();
      });
    } else {
      proceed();
    }
  }

   onGetSystemUserMessage(){ // update message center get system generated messages
    this.messageCenterService.onUpdateSystemUserMessages(true);
  }

  onSubmitReferral(){
    if(this.bindIssuePolicyForm.valid){
        // Validate loss payee premises checkboxes before proceeding
        if (!this.validateLossPayeePremises()) {
          return;
        }
        this.onSubmitReferralinc(this.bindIssuePolicyForm.getRawValue(), false);
    }else{
        this.toastService.addNotification({message: "Please complete and confirm Bind details before Submitting Referral. Providing information in all mandatory fields marked *", type: ToastType.Error}); // error notification
        return;
    }
  }
  
  onSubmitReferralinc(payload: any, isSaveAndExit: boolean){
    const quoteId1 = Number(sessionStorage.getItem("quoteId"));
 const quotePolicyId = this.authService.getUserDetailsFromStorage()?.isPolicyType ? 
    this.authService.getUserDetailsFromStorage()?.policyId : this.selectedQuote()?.quoteId || quoteId1;
    const {policyStartDate, PolicyEndDate, ProductingAgentCompanyName ,productingAgentName, productingAgentEmail, producingAgentPhone,InsuredPhoneNumber, producerName, 
      surplusNumber, insuredContactName, insuredEmailAddress, lossPayeeDetailsForm, producingAgentAddress, 
      insuredMailingAddress, slAddress, triaAcceptance, eqbdAcceptance, slTaxFeeDetails
    } = payload;

    const rawPhoneNumber = producingAgentPhone.replace(/\D/g, '');
    const rawPhoneNumberinsured = InsuredPhoneNumber.replace(/\D/g, '');
    const producingAddressPayload = {
      ...producingAgentAddress,
      quoteId: quotePolicyId,
      unitNum: producingAgentAddress.unitNum?.trim(),
      streetNum: typeof producingAgentAddress.streetNum === 'string' ? producingAgentAddress.streetNum.trim() : producingAgentAddress.streetNum?.toString().trim() || "",
      streetName: producingAgentAddress.streetName?.trim(),
      cityId: producingAgentAddress.cityId ? producingAgentAddress.cityId : 0,
      stateId: producingAgentAddress.stateId ? producingAgentAddress.stateId : 0,
      countyId: producingAgentAddress.countyId ? producingAgentAddress.countyId : 0,
      zipCode: producingAgentAddress.zipCode,
    };    
    
    
    const slAddressPayload = {
      ...slAddress,
      quoteId: quotePolicyId,
      unitNum: slAddress.unitNum?.trim(),
      streetNum: typeof slAddress.streetNum === 'string' ? slAddress.streetNum.trim() : slAddress.streetNum?.toString().trim() || "",
      streetName: slAddress.streetName?.trim(),
      cityId: slAddress.cityId ? slAddress.cityId : 0 ,
      stateId: slAddress.stateId ? slAddress.stateId : 0,
      countyId: slAddress.countyId ? slAddress.countyId : 0,
      zipCode: slAddress.zipCode,
    };
    const insuranceMailingAddressPayload = {
      ...insuredMailingAddress,
      quoteId: quotePolicyId,
      unitNum: insuredMailingAddress.unitNum?.trim(),
      streetNum: typeof insuredMailingAddress.streetNum === 'string' ? insuredMailingAddress.streetNum.trim() : insuredMailingAddress.streetNum?.toString().trim() || "",
      streetName: insuredMailingAddress.streetName?.trim(),
      cityId: insuredMailingAddress.cityId ? insuredMailingAddress.cityId : 0,
      stateId: insuredMailingAddress.stateId ? insuredMailingAddress.stateId : 0,
      countyId: insuredMailingAddress.countyId ? insuredMailingAddress.countyId : 0,
      zipCode: insuredMailingAddress.zipCode,
    };
    const bindIssuePolicyPayload = {
      policyStartDate: policyStartDate,
      PolicyEndDate: PolicyEndDate,
      triaAcceptance: triaAcceptance,
      IsEQBDSelected: Number(eqbdAcceptance) === 1 ? 1 : 0,
      IsEQBDConfirmed:
        eqbdAcceptance === null || eqbdAcceptance === undefined || eqbdAcceptance === ''
          ? null
          : (Number(eqbdAcceptance) === 1 ? 1 : 0),
      lossPayees: this.transformLossPayeeData(lossPayeeDetailsForm)?.lossPayees,
      productingAgentName: productingAgentName?.trim(),
      productingAgentEmail: productingAgentEmail?.trim(),
      ProductingAgentCompanyName: ProductingAgentCompanyName?.trim(),
      producingAgentPhone: rawPhoneNumber,
      InsuredPhoneNumber:rawPhoneNumberinsured,
      producingAgentAddress: producingAddressPayload,
      producerName: producerName?.trim(),
      surplusNumber: surplusNumber === 'Others' 
        ? this.bindIssuePolicyForm.get('surplusOther')?.value?.trim() 
        : surplusNumber?.trim(),
      slAddress: slAddressPayload,
      insuredContactName: insuredContactName?.trim(),
      insuredEmailAddress: insuredEmailAddress?.trim(),
      insuredMailingAddress: insuranceMailingAddressPayload,
      bislFeeDetails: this.transformSurplusFeeData(slTaxFeeDetails, quotePolicyId)
    };

    const proceed = () => {
      if(isSaveAndExit){
        this.handleBindAndIssuePolicySaveByQuoteIdForInceptionDate(bindIssuePolicyPayload,true)
      }else{
        this.handleBindAndIssuePolicySaveByQuoteIdForInceptionDate(bindIssuePolicyPayload,false)
      }
    };

    if (this.isNewJerseyState()) {
      this.saveNJTNData(isSaveAndExit).subscribe((success: boolean) => {
        if (!success) {
          return; // Do not proceed if NJTN save failed
        }
        proceed();
      });
    } else {
      proceed();
    }

 
  }

  validateLossPayeePremises(): boolean {
    const lossPayeeArray = this.lossPayeeItems;
    
    // If no loss payees are added, validation passes
    if (lossPayeeArray.length === 0) {
      return true;
    }
    
    // Check each loss payee to ensure at least one premises is checked
    for (let i = 0; i < lossPayeeArray.length; i++) {
      const premisesArray = this.premisesItems(i);
      let hasCheckedPremises = false;
      
      // Check if at least one premises checkbox is checked for this loss payee
      for (let j = 0; j < premisesArray.length; j++) {
        const premisesControl = premisesArray.at(j);
        if (premisesControl.get('isPremisesChecked')?.value === true) {
          hasCheckedPremises = true;
          break;
        }
      }
      
      // If no premises are checked for this loss payee, show error and return false
      if (!hasCheckedPremises) {
        this.toastService.addNotification({
          message: "Please select at least one premises address for each loss payee before proceeding.",
          type: ToastType.Error
        });
        return false;
      }
    }
    
    return true;
  }

  onRequestAndBindPolicy() {
    const acceptTerms = this.bindIssuePolicyForm.get('termsAndConditions.acceptTerms');
    const materialFacts = this.bindIssuePolicyForm.get('termsAndConditions.materialFacts');
    const slTaxNotice = this.bindIssuePolicyForm.get('termsAndConditions.slTaxNotice');
    this.bindIssuePolicyForm.markAllAsTouched();

    // Validate all non-terms fields first.
    if (this.hasInvalidFieldsExcludingTermsAndConditions()) {
      this.scrollToFirstInvalidField();
      return;
    }

    // Validate terms and conditions last and show toast.
    const hasUncheckedTermsCheckbox =
      !acceptTerms?.value ||
      !materialFacts?.value ||
      !slTaxNotice?.value;
    if (hasUncheckedTermsCheckbox) {
      acceptTerms?.markAsTouched();
      materialFacts?.markAsTouched();
      slTaxNotice?.markAsTouched();
      this.scrollToFirstInvalidField();
      this.toastService.addNotification({
        message: "Please check Terms and Conditions.",
        type: ToastType.Error
      });
      return;
    }

    // Validate loss payee premises checkboxes before proceeding
    if (!this.validateLossPayeePremises()) {
      return;
    }
    this.bindAndIssuePolicyDetailsPayloadHandler(this.bindIssuePolicyForm.getRawValue(), false);
  }

  private hasInvalidFieldsExcludingTermsAndConditions(): boolean {
    const checkControl = (control: AbstractControl | null | undefined, currentPath: string): boolean => {
      if (!control) {
        return false;
      }
      if (currentPath.startsWith('termsAndConditions')) {
        return false;
      }
      if (control instanceof FormGroup) {
        return Object.keys(control.controls).some((key) =>
          checkControl(control.get(key), currentPath ? `${currentPath}.${key}` : key)
        );
      }
      if (control instanceof FormArray) {
        return control.controls.some((child, index) =>
          checkControl(child, `${currentPath}[${index}]`)
        );
      }
      return control.invalid;
    };

    return checkControl(this.bindIssuePolicyForm, '');
  }

  private scrollToFirstInvalidField(): void {
    setTimeout(() => {
      const selectors = [
        '.bind-issue-policy-step-container input.ng-invalid',
        '.bind-issue-policy-step-container select.ng-invalid',
        '.bind-issue-policy-step-container textarea.ng-invalid'
      ];
      for (const selector of selectors) {
        const elements = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
        const firstVisibleInvalid = elements.find((el) => {
          const disabled = (el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).disabled;
          return !disabled && el.offsetParent !== null;
        });
        if (firstVisibleInvalid) {
          firstVisibleInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof firstVisibleInvalid.focus === 'function') {
            firstVisibleInvalid.focus();
          }
          return;
        }
      }
    }, 0);
  }

   getInceptionStatusList(){
            this.indicationQuoteService.InceptionStatusList().subscribe({
            next: (response: any) => {
                this.InceptionStatusList.set(response);
                this.brokerQuoteService.setInceptionStatusList(response);

            },
            error: (error: any) => {

            }
            })
        }

  

  onReturnDashboard(){
    this.indicationQuoteService.removeIndicationQuoteStorageData(); // remove storage data
    this.router.navigate(['/quote']);
  }

   onViewandDownloadDocumentAction(type : string) {
    if(this.isDownloadEmailComplete() === 1) {
      return; // Prevent action if already completed
    }
    if(type === 'download'){
    this.policyDocumentService.setDownloadPolicyDocument(true); // trigger the policy document process
    }else{
      this.policyDocumentService.setEmailDocument(true);      
    }
    // const policyID = this.authService.getUserDetailsFromStorage()?.policyId
    // const usersUrl = this.router.serializeUrl(this.router.createUrlTree(['/policy-document-download', policyID]));
    // window.open(usersUrl, '_blank'); // Opens in a new tab
    // this.policyDocDownload.set(true); // trigger the policy document process
  }

  onDownloadEmailComplete() {
    // Update the signal immediately when API returns 200
    this.isDownloadEmailComplete.set(1);
  }

  checkDownloadEmailCompleteStatus() {
    const policyId = this.authService.getUserDetailsFromStorage()?.policyId;
    if (policyId) {
      this.policyDocumentService.getIsDownloadEmailComplete(Number(policyId)).subscribe({
        next: (response: any) => {
          if (response && response.isDownloadEmailComplete !== undefined) {
            this.isDownloadEmailComplete.set(response.isDownloadEmailComplete);
          }
        },
        error: (error: any) => {
          // Handle error silently or log if needed
        }
      });
    }
  }  

  // New Jersey Transaction Number methods
  checkIfNewJerseyState(addressList: any[]): void {
    if (!addressList || addressList.length === 0) {
      this.isNewJerseyState.set(false);
      this.updateNJTNValidators(false);
      return;
    }
    
    // Get the first address (primary address)
    const primaryAddress = addressList[0];
    const stateName = (primaryAddress?.state || '').toString().toLowerCase().trim();
    
    // Check if state is New Jersey (handle various formats)
    const isNewJersey = stateName === 'new jersey' || 
                        stateName === 'nj' || 
                        stateName.includes('new jersey') ||
                        stateName === 'newjersey';
    
    if (isNewJersey) {
      this.isNewJerseyState.set(true);
      this.updateNJTNValidators(true);
      // Get companyId from the quote data - need to fetch from fetchQuoteClearanceData
      this.fetchCompanyIdAndSlaNumbers();
    } else {
      this.isNewJerseyState.set(false);
      this.updateNJTNValidators(false);
    }
  }

  updateNJTNValidators(isRequired: boolean): void {
    const slaControl = this.bindIssuePolicyForm.get('njtnSlaNumber');
    const yearControl = this.bindIssuePolicyForm.get('njtnYear');
    const uniqueControl = this.bindIssuePolicyForm.get('njtnUniqueNumber');
    
    if (isRequired) {
      slaControl?.setValidators([Validators.required, noWhitespaceValidator]);
      yearControl?.setValidators([Validators.required, noWhitespaceValidator, Validators.pattern(/^\d{2}$/)]);
      uniqueControl?.setValidators([Validators.required, noWhitespaceValidator, Validators.pattern(/^\d{5}$/)]);
    } else {
      slaControl?.clearValidators();
      yearControl?.clearValidators();
      uniqueControl?.clearValidators();
    }
    
    slaControl?.updateValueAndValidity();
    yearControl?.updateValueAndValidity();
    uniqueControl?.updateValueAndValidity();
  }

  fetchCompanyIdAndSlaNumbers(): void {
    // Fetch companyId from fetchQuoteClearanceData response
    const quoteId = this.authService.getUserDetailsFromStorage()?.quoteId;
    if (!quoteId) {
      // Try alternative: get from user details
      const userDetails = this.authService.getUserDetailsFromStorage();
      const brokerCompanyId = userDetails?.companyId;
      if (brokerCompanyId) {
        const companyIdNum = Number(brokerCompanyId);
        this.companyId.set(companyIdNum);
        this.loadSlaNumbers(companyIdNum);
      }
      return;
    }

    this.propertyService.getQuoteClearanceData(Number(quoteId)).subscribe({
      next: (response: any) => {
        // Try different possible locations for companyId
        const compId = response?.quote?.companyId || response?.quote?.brokerId || response?.companyId;
        if (compId) {
          const companyIdNum = Number(compId);
          this.companyId.set(companyIdNum);
          this.loadSlaNumbers(companyIdNum);
        } else {
          // If companyId not found, try to get from user details
          const userDetails = this.authService.getUserDetailsFromStorage();
          const brokerCompanyId = userDetails?.companyId;
          if (brokerCompanyId) {
            const companyIdNum = Number(brokerCompanyId);
            this.companyId.set(companyIdNum);
            this.loadSlaNumbers(companyIdNum);
          }
        }
      },
      error: (error: any) => {
        // Try alternative: get from user details
        const userDetails = this.authService.getUserDetailsFromStorage();
        const brokerCompanyId = userDetails?.companyId;
        if (brokerCompanyId) {
          const companyIdNum = Number(brokerCompanyId);
          this.companyId.set(companyIdNum);
          this.loadSlaNumbers(companyIdNum);
        }
      }
    });
  }

  loadSlaNumbers(companyId: number): void {
    this.systemAdminService.GetNJTNForCompany(companyId).subscribe({
      next: (response: any) => {
        const slaList = Array.isArray(response) ? response.filter((item: any) => item && item.isActive && !item.isDeleted) : [];
        this.slaNumbersList.set(slaList);
        
        // If single SLA number, auto-populate
        if (slaList.length === 1) {
          this.bindIssuePolicyForm.patchValue({
            njtnSlaNumber: slaList[0].slaNumber
          });
        }
        
        // Load existing NJTN data first (it might override year)
        this.loadExistingNJTN();
        
        // Set default year from inception date if not already set
        setTimeout(() => {
          const currentYear = this.bindIssuePolicyForm.get('njtnYear')?.value;
          if (!currentYear) {
            this.setDefaultYear();
          }
        }, 100);
      },
      error: (error: any) => {
        this.loadExistingNJTN();
        
      }
    });
  }

  setDefaultYear(): void {
    const startDate = this.bindIssuePolicyForm.get('policyStartDate')?.value;
    if (startDate) {
      const year = moment(startDate, 'YYYY-MM-DD').format('YY'); // 2-digit year
      this.bindIssuePolicyForm.patchValue({
        njtnYear: year
      });
    }
  }

  // Ensure the bound SLA number is visible in dropdown even if it was removed from active company config list
  private ensureSlaNumberOptionExists(slaNumber: string, configId?: number): void {
    const sla = (slaNumber || '').toString().trim();
    if (!sla) {
      return;
    }
    const current = this.slaNumbersList();
    const alreadyExists = current.some((item: any) => (item?.slaNumber || '').toString().trim() === sla);
    if (alreadyExists) {
      return;
    }
    this.slaNumbersList.set([
      ...current,
      {
        configId: Number(configId) || 0,
        slaNumber: sla,
        isActive: true,
        isDeleted: false
      }
    ]);
  }

  loadExistingNJTN(): void {
    if (this.authService.isPolicyType()) {      
      // Load from policy
      const policyId = Number(this.authService.getUserDetailsFromStorage()?.policyId);      
      if (policyId) {
        this.brokerQuoteService.GetPolicyNJTN(policyId).subscribe({
          next: (response: any) => {
            if (response) {
              this.existingNJTNList.set([response]);
              // Parse the njtn string to extract unique number
              // Format: "AYP-445" or "NJTN-2026-00045" or just the unique number
              const njtnString = response.njtn || '';
              
              const uniqueNumber = this.extractUniqueNumberFromNJTN(njtnString);
              
              // Bind SLA Number directly from API response
              const slaNumber = (response?.slaNumber || '').toString().trim();
              this.ensureSlaNumberOptionExists(slaNumber, response?.configId);
              
              // Convert full year to 2-digit for display
              const year = response.year ? String(response.year).slice(-2) : '';
              
              this.bindIssuePolicyForm.patchValue({
                njtnSlaNumber: slaNumber,
                njtnYear: year,
                njtnUniqueNumber: uniqueNumber
              });
            }
          },
          error: (error: any) => {
          }
        });
      }
    } else {
      // Load from quote
      const quoteId = Number(sessionStorage.getItem('quoteId')) || Number(this.authService.getUserDetailsFromStorage()?.quoteId);
      if (quoteId) {
        this.brokerQuoteService.GetQuoteNJTN(quoteId).subscribe({
          next: (response: any) => {
           if (response) {
              this.existingNJTNList.set([response]);
              
              // Parse the njtn string to extract unique number
              // Format: "AYP-445" or "NJTN-2026-00045" or just the unique number
              const njtnString = response.njtn || '';
              const uniqueNumber = this.extractUniqueNumberFromNJTN(njtnString);
              
              // Find the SLA number from configId
              const slaItem = this.slaNumbersList().find(item => item.configId === response.configId);
              const slaNumber = slaItem?.slaNumber || '';
              
              // Convert full year to 2-digit for display
              const year = response.year ? String(response.year).slice(-2) : '';
              
              this.bindIssuePolicyForm.patchValue({
                njtnSlaNumber: slaNumber,
                njtnYear: year,
                njtnUniqueNumber: uniqueNumber
              });
            }
          },
          error: (error: any) => {
          }
        });
      }
    }
  }

  extractUniqueNumberFromNJTN(njtnString: string): string {
    if (!njtnString) return '';
    
    // Handle formats like "NJTN-2026-00045", "AYP-445", or just "00045"
    const parts = njtnString.split('-');
    
    // If it's format "NJTN-2026-00045", get the last part
    if (parts.length === 3) {
      return parts[2];
    }
    // If it's format "AYP-445", get the last part
    if (parts.length === 2) {
      return parts[1];
    }
    // If it's just a number, return it
    if (/^\d+$/.test(njtnString)) {
      return njtnString.padStart(5, '0'); // Ensure 5 digits
    }
    
    return '';
  }

  saveNJTNData(skipValidationForSaveExit: boolean = false) {
    const slaNumber = this.bindIssuePolicyForm.get('njtnSlaNumber')?.value;
    const year = this.bindIssuePolicyForm.get('njtnYear')?.value;
    const uniqueNumber = this.bindIssuePolicyForm.get('njtnUniqueNumber')?.value;
    const hasAnyNjtnInput = !!(slaNumber || year || uniqueNumber);
    // Get existing NJTN (if any) so we can pass slTransactionId back to the API
    const existingList = this.existingNJTNList();
    const existingNJTN = existingList && existingList.length > 0 ? existingList[0] : null;
    // Save & Exit should reflect exactly what's currently in the form (including cleared values).
    // For regular submit flows, keep strict validation behavior.
    const effectiveSlaNumber = String(slaNumber ?? '').trim();
    const effectiveYear = String(year ?? '').trim();
    const effectiveUniqueNumber = String(uniqueNumber ?? '').trim();

    // If not NJ and no NJTN data entered, nothing to save.
    if (!this.isNewJerseyState() && !hasAnyNjtnInput) {
      return of(true);
    }
    
    if (!effectiveSlaNumber || !effectiveYear || !effectiveUniqueNumber) {
      if (skipValidationForSaveExit) {
        // Save & Exit should still attempt API call; if data is incomplete, do not block exit.
      } else {
        return of(false); // Not all fields filled
      }
    }
    
    // Resolve configId using selected SLA; fallback to existing bind response configId
    // when SLA exists in bind API but is no longer present in active company config list.
    const slaItem = this.slaNumbersList().find(item => item.slaNumber === effectiveSlaNumber);
    const resolvedConfigId = effectiveSlaNumber
      ? (
        (slaItem && Number(slaItem.configId)) ||
        ((existingNJTN?.slaNumber || '').toString().trim() === effectiveSlaNumber
          ? Number(existingNJTN?.configId)
          : 0)
      )
      : 0;

    if (!resolvedConfigId) {
      if (!skipValidationForSaveExit) {
        this.toastService.addNotification({
          message: 'Invalid SLA number selected.',
          type: ToastType.Error
        });
        return of(false);
      }
    }
    
    const createdUserId = Number(this.authService.getUserDetailsFromStorage()?.createdUserId || 
                                  this.authService.getUserDetailsFromStorage()?.userDetails?.userId);
    
    if (this.authService.isPolicyType()) {
      const policyId = Number(this.authService.getUserDetailsFromStorage()?.policyId);
      if (policyId) {
        // For policy: year is full year (2026), njtn is unique number part
        const fullYear = Number('20' + effectiveYear); // Convert 2-digit to full year
        const payload: any = {
          policyId: policyId,
          configId: resolvedConfigId || 0,
          slaNumber: effectiveSlaNumber,
          year: fullYear,
          njtn: effectiveUniqueNumber,
          createdUserId: createdUserId,
          createdDate: new Date().toISOString(),
          slTransactionId: existingNJTN?.slTransactionId || 0
        };
        return this.brokerQuoteService.SavePolicyNJTN(payload).pipe(
          tap((response: any) => {
            if (response) {              
              this.loadExistingNJTN(); // Reload to update list
            }
          }),
          map(() => true),
          catchError((error: any) => {
            if (!skipValidationForSaveExit) {
              if (error?.status === 500) {
                this.toastService.addNotification({
                  message: 'Number already exists, please provide a unique number.',
                  type: ToastType.Error
                });
              } else {
                this.toastService.addNotification({
                  message: 'System error. Failed to save NJTN. Please exit and contact help@AbacusAU.com.',
                  type: ToastType.Error
                });
              }
            }
            return of(skipValidationForSaveExit ? true : false);
          })
        );
      }
      return of(skipValidationForSaveExit ? true : false);
    } else {
      const quoteId = Number(sessionStorage.getItem('quoteId')) || Number(this.authService.getUserDetailsFromStorage()?.quoteId);
      if (quoteId) {
        // For quote: year is 2-digit (26), njtn is unique number part
        const payload: any = {
          quoteId: quoteId,
          configId: resolvedConfigId || 0,
          slaNumber: effectiveSlaNumber,
          year: Number(effectiveYear), // 2-digit year
          njtn: effectiveUniqueNumber,
          createdUserId: createdUserId,
          slTransactionId: existingNJTN?.slTransactionId || 0
        };
        return this.brokerQuoteService.SaveQuoteNJTN(payload).pipe(
          tap((response: any) => {
            if (response) {              
              this.loadExistingNJTN(); // Reload to update list
            }
          }),
          map(() => true),
          catchError((error: any) => {
            if (!skipValidationForSaveExit) {
              if (error?.status === 500) {
                this.toastService.addNotification({
                  message: 'Number already exists, please provide a unique number.',
                  type: ToastType.Error
                });
              } else {
                this.toastService.addNotification({
                  message: 'System error. Failed to save NJTN. Please exit and contact help@AbacusAU.com.',
                  type: ToastType.Error
                });
              }
            }
            return of(skipValidationForSaveExit ? true : false);
          })
        );
      }
      return of(skipValidationForSaveExit ? true : false);
    }
  }

  ngOnDestroy(): void {
    if(this.navLogoClickSubscription){
      this.navLogoClickSubscription.unsubscribe();
    }
  }
}

